/**
 * LandingPageBuilder — Standalone 3-Panel LP Builder
 *
 * Route: /app/landing-pages/:landing_page_id
 *
 * System Alignment (010C2):
 *   - Uses EXACT SAME tokens as FunnelDetailsPage + VisualFunnelBuilder
 *   - Selection state = FunnelStepsPanel (border-l-2 accent)
 *   - Viewport locked: h-full overflow-hidden, panel-only scroll
 *   - Background: bg-white/[0.02] (same as FunnelDetailsPage)
 *   - Tabs are real: Builder | Settings | History | (Analytics/Embed disabled)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Save, Globe, Loader2, Check, AlertCircle,
  Wrench, BarChart3, Code2, Settings, History,
  Plus, X, Monitor, Smartphone, Copy, ChevronDown,
  ShieldCheck, ListOrdered, UserCheck, UserX, ArrowRight,
  CheckCircle, XCircle, HelpCircle, Target, LayoutGrid,
  MousePointerClick, AlertTriangle, Layers, Award,
  GitBranch, Clock, FileText, Info, GripVertical, Trash2,
  ExternalLink, Archive, Link2, AlertCircle as LucideAlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { toast } from "@/hooks/use-toast";
import { useEditorMode } from "@/contexts/PageTitleContext";
import { toBuilderSections, getPopulatedSections, sectionHasData, SECTION_ORDER } from "@/lib/normalizeSnapshot";
// MA-LP-RENDER-ALIGN-007: Aligned preview renderers mirror backend lp_section_renderers.py
// MA-LP-LAYOUT-027B + MA-LP-THEME-036: Dynamic token resolution via single theme source
import { renderPreviewSection, hasPreviewContent, SURFACE_CONFIG, setActiveBrand, getSurfaceConfig } from "@/components/lp-builder/LPSectionPreview";
import { extractLPTheme } from "@/lib/resolveThemeSource";
import { resolveLpTheme, resolveTheme, PRESETS } from "@/lib/themeEngine";

// ═══════════════════════════════════════════════════════════════
// SECTION CONFIGURATION
//
// TRIAD RULE (MA-LP-ARCH-008): New visible section types require ALL THREE:
//   1. Backend render   — add render_<type>() in lp_section_renderers.py
//   2. Preview render   — add case in LPSectionPreview.jsx renderPreviewSection()
//   3. Builder editing  — add entry here (icon, fields, label) + SECTION_DEFAULTS
// Omitting any layer causes preview/live drift or uneditable sections.
// Non-visible types (e.g. seo) are exempt from preview render requirement.
// ═══════════════════════════════════════════════════════════════

const SECTION_CONFIG = {
  hero: {
    label: "Hero", icon: LayoutGrid, color: "text-indigo-400",
    fields: [
      { key: "headline", label: "Headline", type: "textarea", rows: 2 },
      { key: "subheadline", label: "Subheadline", type: "textarea", rows: 2 },
      { key: "cta_label", label: "CTA Button Text", type: "text" },
      { key: "cta_url", label: "CTA Destination", type: "text", placeholder: "#funnel-anchor" },
      { key: "reassurance_line", label: "Reassurance Line", type: "text" },
      { key: "supporting_text", label: "Supporting Text", type: "textarea", rows: 2 },
    ],
  },
  trust_signals: {
    label: "Trust Signals", icon: ShieldCheck, color: "text-emerald-400",
    fields: [{ key: "_items", label: "Trust Signals", type: "trust_signals_list" }],
  },
  how_it_works: {
    label: "How It Works", icon: ListOrdered, color: "text-sky-400",
    fields: [
      { key: "headline", label: "Headline", type: "text" },
      { key: "subheadline", label: "Subheadline", type: "text" },
      { key: "steps", label: "Steps", type: "steps" },
    ],
  },
  personal_authority: {
    label: "Personal Authority", icon: Award, color: "text-violet-400",
    fields: [
      { key: "profile_image_url", label: "Profile Image URL", type: "text", placeholder: "https://..." },
      { key: "name", label: "Name", type: "text" },
      { key: "title", label: "Title", type: "text", placeholder: "Licensed Protection Specialist" },
      { key: "subtitle", label: "Subtitle", type: "text", placeholder: "Short role descriptor" },
      { key: "body_text", label: "Body Text", type: "textarea", rows: 3 },
      { key: "reassurance_text", label: "Reassurance", type: "text", placeholder: "One friction-reducing sentence" },
    ],
  },
  mid_cta: {
    label: "Mid CTA", icon: MousePointerClick, color: "text-rose-400",
    fields: [
      { key: "headline", label: "Headline", type: "text" },
      { key: "subheadline", label: "Subheadline", type: "text" },
      { key: "button_text", label: "Button Text", type: "text" },
      { key: "cta_url", label: "CTA Destination", type: "text", placeholder: "#funnel-anchor" },
      { key: "supporting_text", label: "Supporting Text", type: "text" },
      { key: "urgency_text", label: "Urgency Text", type: "text" },
    ],
  },
  who_this_is_for: {
    label: "Who This Is For", icon: UserCheck, color: "text-green-400",
    fields: [
      { key: "headline", label: "Headline", type: "text" },
      { key: "bullets", label: "Points", type: "list" },
    ],
  },
  who_this_is_not_for: {
    label: "Who This Is Not For", icon: UserX, color: "text-amber-400",
    fields: [
      { key: "headline", label: "Headline", type: "text" },
      { key: "bullets", label: "Points", type: "list" },
    ],
  },
  what_happens_next: {
    label: "What Happens Next", icon: ArrowRight, color: "text-indigo-400",
    fields: [
      { key: "headline", label: "Headline", type: "text" },
      { key: "bullets", label: "Steps", type: "list" },
      { key: "reassurance", label: "Reassurance", type: "text" },
    ],
  },
  what_this_is: {
    label: "What This Is", icon: CheckCircle, color: "text-teal-400",
    fields: [
      { key: "headline", label: "Headline", type: "text" },
      { key: "bullets", label: "Points", type: "list" },
    ],
  },
  what_this_is_not: {
    label: "What This Is Not", icon: XCircle, color: "text-orange-400",
    fields: [
      { key: "headline", label: "Headline", type: "text" },
      { key: "bullets", label: "Points", type: "list" },
    ],
  },
  faq: {
    label: "FAQ", icon: HelpCircle, color: "text-purple-400",
    fields: [{ key: "_items", label: "Questions", type: "faq_list" }],
  },
  final_cta: {
    label: "Final CTA", icon: Target, color: "text-rose-400",
    fields: [
      { key: "headline", label: "Headline", type: "text" },
      { key: "body", label: "Body", type: "textarea", rows: 3 },
      { key: "button_text", label: "Button Text", type: "text" },
      { key: "cta_url", label: "CTA Destination", type: "text", placeholder: "#funnel-anchor" },
      { key: "supporting_text", label: "Supporting Text", type: "text" },
    ],
  },
  seo: {
    label: "SEO & Meta", icon: Globe, color: "text-cyan-400",
    fields: [
      { key: "page_title", label: "Page Title", type: "text", maxLength: 60 },
      { key: "meta_description", label: "Meta Description", type: "textarea", rows: 2, maxLength: 160 },
      { key: "og_title", label: "OG Title", type: "text" },
      { key: "og_description", label: "OG Description", type: "text" },
    ],
  },
  funnel_embed: {
    label: "Funnel Embed", icon: Code2, color: "text-rose-400",
    fields: [
      { key: "funnel_id", label: "Select Funnel", type: "funnel_selector" },
      { key: "adapt_lp_theme", label: "Adapt to Landing Page Theme", type: "toggle", hint: "When on, the funnel inherits the Landing Page color scheme instead of its own." },
      { key: "headline", label: "Headline", type: "text" },
      { key: "subheadline", label: "Subheadline", type: "text" },
      { key: "layout", label: "Layout", type: "layout_selector" },
      { key: "height", label: "Embed Height (px)", type: "number" },
    ],
  },
};

// ═══════════════════════════════════════════════════════════════
// SECTION OPERATION CONSTRAINTS (Phase 3)
// ═══════════════════════════════════════════════════════════════

// Singleton types: only one instance allowed
const SINGLETON_TYPES = new Set(["hero", "seo", "funnel_embed"]);

// Types requiring warning before deletion
const WARN_DELETE_TYPES = new Set(["hero", "final_cta"]);

// Default data for newly ADDED sections — must survive getPopulatedSections filter.
// Includes one empty template item/field so sectionHasData returns true.
// Auto-generated empty slots from toBuilderSections() still use bare {} / [] (filtered on save).
const SECTION_DEFAULTS = {
  hero: { headline: "", subheadline: "", cta_label: "", cta_url: "" },
  trust_signals: [{ text: "" }],
  how_it_works: { headline: "" },
  personal_authority: { profile_image_url: "", name: "", title: "", subtitle: "", body_text: "", reassurance_text: "" },
  mid_cta: { headline: "", cta_label: "", cta_url: "" },
  who_this_is_for: { headline: "" },
  who_this_is_not_for: { headline: "" },
  what_happens_next: { headline: "" },
  what_this_is: { headline: "" },
  what_this_is_not: { headline: "" },
  faq: [{ q: "", a: "" }],
  final_cta: { headline: "", cta_label: "", cta_url: "" },
  seo: { page_title: "", meta_description: "" },
  funnel_embed: { funnel_id: "", embed_key: "", funnel_name: "", headline: "", subheadline: "", layout: "centered", height: 280, adapt_lp_theme: false },
};

function getDefaultData(type) {
  // Deep clone to avoid mutation across instances
  return JSON.parse(JSON.stringify(SECTION_DEFAULTS[type] || {}));
}

// ═══════════════════════════════════════════════════════════════
// SORTABLE SECTION ITEM (Phase 3: drag-and-drop + delete)
// ═══════════════════════════════════════════════════════════════

function SortableSectionItem({ section, idx, config, hasData, isActive, isEditable, canDelete, onSelect, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
    position: isDragging ? "relative" : undefined,
  };

  const Icon = config.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(section.id)}
      className={cn(
        "group flex items-center gap-1.5 h-9 px-1.5 rounded-lg",
        "text-left transition-colors duration-150 cursor-pointer",
        isActive && ["bg-indigo-500/15", "border-l-2 border-l-indigo-400 -ml-0.5 pl-[7px]"],
        !isActive && "hover:bg-white/5"
      )}
      data-testid={`nav-section-${section.type}`}
    >
      {/* Drag handle — visible only on editable drafts */}
      {isEditable ? (
        <button
          {...attributes}
          {...listeners}
          className="w-4 flex items-center justify-center flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
          data-testid={`drag-handle-${section.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-3 w-3 text-white/20 group-hover:text-white/50 transition-colors" />
        </button>
      ) : (
        <span className="w-4 text-xs text-white/40 text-right flex-shrink-0">{idx + 1}</span>
      )}
      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0">
        <Icon className={cn("h-3.5 w-3.5", config.color)} />
      </div>
      <span className={cn("flex-1 text-sm truncate", isActive ? "text-slate-100 font-medium" : "text-slate-300")}>
        {config.label}
      </span>
      {/* Delete button — visible on hover for editable drafts */}
      {isEditable && canDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(section); }}
          className="w-5 h-5 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all flex-shrink-0"
          data-testid={`delete-section-${section.id}`}
        >
          <Trash2 className="h-3 w-3 text-red-400/70 hover:text-red-400" />
        </button>
      )}
      {/* Populated indicator */}
      {hasData ? (
        <CheckCircle className="w-3.5 h-3.5 text-emerald-500/60 flex-shrink-0" />
      ) : (
        <div className="w-3.5 h-3.5 rounded-full border border-white/[0.1] flex-shrink-0" />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// FIELD EDITORS
// ═══════════════════════════════════════════════════════════════

function ListFieldEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (idx, val) => { const u = [...items]; u[idx] = val; onChange(u); };
  const addItem = () => onChange([...items, ""]);
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex gap-1.5">
          <Input value={typeof item === "string" ? item : item?.text || ""} onChange={(e) => updateItem(i, e.target.value)}
            className="h-8 text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50 flex-1" placeholder={`Item ${i + 1}`} data-testid={`list-item-${i}`} />
          <button onClick={() => removeItem(i)} className="text-slate-600 hover:text-red-400 p-1 shrink-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button onClick={addItem} className="text-[10px] text-indigo-400/70 hover:text-indigo-300 flex items-center gap-1" data-testid="add-list-item"><Plus className="w-3 h-3" /> Add item</button>
    </div>
  );
}

function StepsFieldEditor({ value, onChange }) {
  const steps = Array.isArray(value) ? value : [];
  const updateStep = (idx, field, val) => { const u = [...steps]; u[idx] = { ...u[idx], [field]: val }; onChange(u); };
  const addStep = () => onChange([...steps, { title: "", description: "" }]);
  const removeStep = (idx) => onChange(steps.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2">
      {steps.map((step, i) => (
        <div key={i} className="p-2.5 rounded-lg bg-white/5 border border-white/10 space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-slate-500 font-medium">Step {i + 1}</span>
            <button onClick={() => removeStep(i)} className="text-slate-600 hover:text-red-400"><X className="w-3 h-3" /></button>
          </div>
          <Input value={step.title || ""} onChange={(e) => updateStep(i, "title", e.target.value)}
            className="h-7 text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50" placeholder="Step title" data-testid={`step-title-${i}`} />
          <Textarea value={step.description || ""} onChange={(e) => updateStep(i, "description", e.target.value)}
            className="min-h-[40px] text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50 resize-y" placeholder="Description" data-testid={`step-desc-${i}`} />
        </div>
      ))}
      <button onClick={addStep} className="text-[10px] text-indigo-400/70 hover:text-indigo-300 flex items-center gap-1" data-testid="add-step"><Plus className="w-3 h-3" /> Add step</button>
    </div>
  );
}

// MA-LP-LAYOUT-027: Trust Signal Icon Picker
// Shares icon library with LPSectionPreview.jsx TRUST_ICONS
const TRUST_ICON_MAP = {
  shield: "Shield", check: "Check", clock: "Clock", lock: "Lock", star: "Star",
  heart: "Heart", "thumbs-up": "Thumbs Up", phone: "Phone", award: "Award", users: "Users",
};

function TrustIconPreview({ iconKey }) {
  const iconSvgs = {
    shield: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
    clock: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    lock: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>,
    star: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    heart: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    "thumbs-up": <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>,
    phone: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
    award: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>,
    users: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  };
  return <span className="text-slate-400">{iconSvgs[iconKey] || iconSvgs.shield}</span>;
}

function TrustIconGrid({ selected, onSelect }) {
  return (
    <div data-testid="trust-icon-grid" className="grid grid-cols-5 gap-1 p-2 rounded-lg bg-slate-900 border border-white/10">
      {Object.entries(TRUST_ICON_MAP).map(([key, label]) => (
        <button
          key={key}
          type="button"
          data-testid={`trust-icon-option-${key}`}
          onClick={() => onSelect(key)}
          title={label}
          className={cn(
            "w-8 h-8 rounded-md flex items-center justify-center transition-colors",
            selected === key
              ? "bg-indigo-500/20 border border-indigo-500/40 text-indigo-300"
              : "bg-white/5 border border-transparent hover:border-white/10 text-slate-500 hover:text-slate-300"
          )}
        >
          <TrustIconPreview iconKey={key} />
        </button>
      ))}
    </div>
  );
}


function TrustSignalsEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const MAX_TRUST_SIGNALS = 3;
  const [showIconPicker, setShowIconPicker] = useState(false);
  // MA-LP-UX-043: Single icon for all items — derived from first item
  const sectionIcon = (items[0]?.icon) || "shield";
  const updateItem = (idx, field, val) => { const u = [...items]; u[idx] = { ...u[idx], [field]: val }; onChange(u); };
  const addItem = () => { if (items.length < MAX_TRUST_SIGNALS) onChange([...items, { icon: sectionIcon, text: "" }]); };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  const atLimit = items.length >= MAX_TRUST_SIGNALS;
  // Change icon for ALL items at once
  const setAllIcons = (key) => {
    const updated = items.map(it => ({ ...it, icon: key }));
    onChange(updated);
    setShowIconPicker(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] text-slate-400 uppercase tracking-wider">Icon (all)</span>
        <button
          type="button"
          data-testid="trust-icon-picker-all"
          onClick={() => setShowIconPicker(!showIconPicker)}
          className={cn(
            "w-7 h-7 rounded-md border flex items-center justify-center shrink-0 transition-colors",
            showIconPicker
              ? "border-indigo-500/50 bg-indigo-500/10"
              : "border-white/10 bg-white/5 hover:border-white/20"
          )}
          title="Change icon for all trust signals"
        >
          <TrustIconPreview iconKey={sectionIcon} />
        </button>
      </div>
      {showIconPicker && (
        <TrustIconGrid
          selected={sectionIcon}
          onSelect={setAllIcons}
          onClose={() => setShowIconPicker(false)}
        />
      )}
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-center">
          <div className="w-6 h-6 flex items-center justify-center shrink-0 opacity-50">
            <TrustIconPreview iconKey={sectionIcon} />
          </div>
          <Input value={item.text || (typeof item === "string" ? item : "")} onChange={(e) => updateItem(i, "text", e.target.value)}
            className="h-8 text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50 flex-1" placeholder="1-2 words" data-testid={`trust-signal-${i}`} />
          <button onClick={() => removeItem(i)} className="text-slate-600 hover:text-red-400 p-1 shrink-0" data-testid={`remove-trust-signal-${i}`}><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {!atLimit && (
        <button onClick={addItem} className="text-[10px] text-indigo-400/70 hover:text-indigo-300 flex items-center gap-1" data-testid="add-trust-signal"><Plus className="w-3 h-3" /> Add trust signal</button>
      )}
      {atLimit && (
        <span className="text-[10px] text-slate-500" data-testid="trust-signal-limit">Maximum {MAX_TRUST_SIGNALS} trust signals</span>
      )}
    </div>
  );
}

function FaqEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (idx, field, val) => { const u = [...items]; u[idx] = { ...u[idx], [field]: val }; onChange(u); };
  const addItem = () => onChange([...items, { question: "", answer: "" }]);
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="p-2.5 rounded-lg bg-white/5 border border-white/10 space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-slate-500 font-medium">Q&A {i + 1}</span>
            <button onClick={() => removeItem(i)} className="text-slate-600 hover:text-red-400"><X className="w-3 h-3" /></button>
          </div>
          <Input value={item.question || ""} onChange={(e) => updateItem(i, "question", e.target.value)}
            className="h-7 text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50" placeholder="Question" data-testid={`faq-q-${i}`} />
          <Textarea value={item.answer || ""} onChange={(e) => updateItem(i, "answer", e.target.value)}
            className="min-h-[40px] text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50 resize-y" placeholder="Answer" data-testid={`faq-a-${i}`} />
        </div>
      ))}
      <button onClick={addItem} className="text-[10px] text-indigo-400/70 hover:text-indigo-300 flex items-center gap-1" data-testid="add-faq"><Plus className="w-3 h-3" /> Add Q&A pair</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SECTION PREVIEW — renders inside center canvas
// ═══════════════════════════════════════════════════════════════
// RENDER TARGETS:
//   Builder mode = editor-first render target (fast, compact, chrome-heavy)
//   Preview mode = page-preview render target aligned to served LP
//                  (uses LPSectionPreview.jsx which mirrors backend renderers)
// Preview logic must NOT drift independently from served LP render behavior.
// ═══════════════════════════════════════════════════════════════

function SectionPreviewBlock({ sectionKey, data, isSelected, onClick, isPreviewMode, allSectionData, previewMode }) {
  const config = SECTION_CONFIG[sectionKey];
  if (!config) return null;

  // ──── PREVIEW MODE: Aligned to served LP (MA-LP-RENDER-ALIGN-007) ────
  // Delegates to LPSectionPreview.jsx renderers which mirror lp_section_renderers.py
  if (isPreviewMode) {
    if (!hasPreviewContent(sectionKey, data)) return null;

    const surfaceBg = getSurfaceConfig()[sectionKey]?.bg;
    return (
      <div
        id={`preview-section-${sectionKey}`}
        onClick={onClick}
        style={surfaceBg ? { background: surfaceBg, padding: "40px 20px" } : undefined}
        className={cn(
          "cursor-pointer transition-all duration-200 relative",
          isSelected
            ? "ring-1 ring-indigo-400/30 ring-inset"
            : "hover:ring-1 hover:ring-white/[0.06] hover:ring-inset"
        )}
        data-testid={`preview-${sectionKey}`}
      >
        {renderPreviewSection(sectionKey, data, allSectionData)}
      </div>
    );
  }

  // ──── BUILDER MODE: Editor-optimized rendering (unchanged) ────
  const Icon = config.icon;
  const isEmpty = !data || (Array.isArray(data) && data.length === 0) ||
    (typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0);

  return (
    <div
      id={`preview-section-${sectionKey}`}
      onClick={onClick}
      className={cn(
        "px-6 py-5 border-b border-white/[0.06] cursor-pointer transition-all duration-150 relative group",
        isSelected && "bg-indigo-500/[0.08] border-l-2 border-l-indigo-400",
        !isSelected && "hover:bg-white/[0.03]"
      )}
      data-testid={`preview-${sectionKey}`}
    >
      <div className={cn("flex items-center gap-2 mb-3 transition-opacity", isSelected ? "opacity-100" : "opacity-40 group-hover:opacity-70")}>
        <Icon className={cn("w-3.5 h-3.5", isSelected ? config.color : "text-slate-500")} />
        <span className="text-[10px] uppercase tracking-wider font-medium text-slate-500">{config.label}</span>
      </div>

      {isEmpty ? (
        <p className="text-xs text-slate-600 italic">No content — click to start editing</p>
      ) : sectionKey === "hero" ? (
        <div className="text-center space-y-2.5 py-3">
          {data.headline && <h2 className="text-xl font-bold text-white leading-tight">{data.headline}</h2>}
          {data.subheadline && <p className="text-sm text-slate-400 max-w-lg mx-auto">{data.subheadline}</p>}
          {data.cta_label && <div className="inline-block mt-2 px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg">{data.cta_label}</div>}
          {data.supporting_text && <p className="text-xs text-slate-500 mt-1">{data.supporting_text}</p>}
        </div>
      ) : sectionKey === "trust_signals" ? (
        (() => {
          const isMobile = previewMode === "mobile";
          const tsItems = (Array.isArray(data) ? data : []).slice(0, 3);
          const sIcon = tsItems[0]?.icon || "shield";
          return (
            <div className={cn("flex items-center justify-center flex-nowrap overflow-hidden py-2", isMobile ? "gap-2" : "gap-3")}>
              {tsItems.map((ts, i) => (
                <div key={i} className="flex items-center gap-1 shrink min-w-0 whitespace-nowrap">
                  <TrustIconPreview iconKey={sIcon} />
                  <span className={cn("text-slate-300 font-semibold tracking-wide", isMobile ? "text-[10px]" : "text-xs")}>{typeof ts === "string" ? ts : ts?.text || ""}</span>
                </div>
              ))}
            </div>
          );
        })()
      ) : sectionKey === "how_it_works" ? (
        <div className="space-y-2">
          {data.headline && <p className="text-sm font-semibold text-white">{data.headline}</p>}
          {data.subheadline && <p className="text-xs text-slate-400">{data.subheadline}</p>}
          {(data.steps || []).map((step, i) => (
            <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-white/5 border border-white/[0.06]">
              <span className="w-6 h-6 rounded-full bg-indigo-600/25 text-indigo-300 text-xs flex items-center justify-center shrink-0 font-bold">{i + 1}</span>
              <div className="min-w-0">
                <span className="text-xs font-medium text-slate-200">{step.title}</span>
                {step.description && <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{step.description}</p>}
              </div>
            </div>
          ))}
        </div>
      ) : sectionKey === "faq" ? (
        <div className="space-y-2">
          {(Array.isArray(data) ? data : data?.items || []).map((item, i) => (
            <div key={i} className="p-3 rounded-lg bg-white/5 border border-white/[0.06]">
              <p className="text-xs font-medium text-slate-200">{item.question || item.q}</p>
              <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{item.answer || item.a}</p>
            </div>
          ))}
        </div>
      ) : (sectionKey === "mid_cta" || sectionKey === "final_cta") ? (
        <div className="text-center space-y-2 py-3">
          {data.headline && <p className="text-base font-semibold text-white">{data.headline}</p>}
          {data.subheadline && <p className="text-xs text-slate-400">{data.subheadline}</p>}
          {data.body && <p className="text-xs text-slate-400 max-w-md mx-auto">{data.body}</p>}
          {data.button_text && <div className="inline-block mt-2 px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg">{data.button_text}</div>}
          {data.supporting_text && <p className="text-[11px] text-slate-500 mt-1">{data.supporting_text}</p>}
        </div>
      ) : sectionKey === "personal_authority" ? (
        <div className="text-center space-y-3 py-3">
          <div className="flex flex-col items-center gap-1.5">
            {data.profile_image_url ? (
              <img src={data.profile_image_url} alt={data.name || data.title} className="w-12 h-12 rounded-full object-cover border border-white/10" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-500"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
            )}
            {data.name && <span className="text-xs font-medium text-slate-300">{data.name}</span>}
            {data.title && <span className="text-sm font-semibold text-white">{data.title}</span>}
            {data.subtitle && <span className="text-[11px] text-slate-500">{data.subtitle}</span>}
          </div>
          {data.body_text && <p className="text-xs text-slate-300 leading-relaxed max-w-md mx-auto">{data.body_text}</p>}
          {data.reassurance_text && (
            <div className="mx-auto max-w-sm px-3 py-2 rounded-lg bg-white/5 border border-white/[0.06]">
              <p className="text-[11px] text-slate-400 italic">{data.reassurance_text}</p>
            </div>
          )}
        </div>
      ) : sectionKey === "funnel_embed" ? (
        <div className="text-center space-y-2 py-3">
          {data.funnel_name ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/20">
                <Code2 className="w-3.5 h-3.5 text-rose-400" />
                <span className="text-xs font-medium text-rose-300">{data.funnel_name}</span>
              </div>
              {data.headline && <p className="text-sm font-semibold text-white mt-2">{data.headline}</p>}
              {data.subheadline && <p className="text-xs text-slate-400">{data.subheadline}</p>}
              <p className="text-[10px] text-slate-600">{data.layout === "full" ? "Full width" : "Centered"} | {data.height}px</p>
            </>
          ) : (
            <p className="text-xs text-slate-600 italic">No funnel selected — use inspector to choose</p>
          )}
        </div>
      ) : sectionKey === "seo" ? (
        <div className="space-y-2">
          {data.page_title && <div className="p-2.5 rounded-lg bg-white/5 border border-white/[0.06]"><span className="text-[10px] text-slate-500">Title</span><p className="text-xs text-slate-200 mt-0.5">{data.page_title}</p></div>}
          {data.meta_description && <div className="p-2.5 rounded-lg bg-white/5 border border-white/[0.06]"><span className="text-[10px] text-slate-500">Description</span><p className="text-xs text-slate-300 mt-0.5">{data.meta_description}</p></div>}
        </div>
      ) : (
        <div className="space-y-2">
          {data.headline && <p className="text-sm font-semibold text-white">{data.headline}</p>}
          {data.body && <p className="text-xs text-slate-300 leading-relaxed">{data.body}</p>}
          {(data.bullets || data.items || data.points || []).map((item, i) => (
            <div key={i} className="flex items-start gap-2 text-xs"><span className="text-indigo-400/60 mt-0.5 shrink-0">&#x2022;</span><span className="text-slate-300">{typeof item === "string" ? item : item?.text || item?.title || ""}</span></div>
          ))}
          {data.reassurance && <p className="text-[11px] text-slate-500 mt-2 italic">{data.reassurance}</p>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FUNNEL SELECTOR EDITOR (Phase 4: funnel_embed inspector)
// ═══════════════════════════════════════════════════════════════

function FunnelSelectorEditor({ sectionId, sectionData, onFieldChange, isEditable }) {
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // DEBUG: Check localStorage token on every render
  useEffect(() => {
    const token = getToken();
    const rawToken = localStorage.getItem('scriptcompass_token');
    console.log('[FunnelSelector] Component mounted');
    console.log('[FunnelSelector] getToken() result:', token ? `${token.substring(0, 20)}...` : 'null');
    console.log('[FunnelSelector] localStorage direct:', rawToken ? `${rawToken.substring(0, 20)}...` : 'null');
    console.log('[FunnelSelector] localStorage keys:', Object.keys(localStorage));
  }, []);

  const fetchFunnels = useCallback(async () => {
    // Guard: Only fetch if token exists (prevents "Missing bearer token" error)
    const token = getToken();
    console.log('[LP Builder] fetchFunnels called - token exists:', !!token);
    if (!token) {
      console.error('[LP Builder] No auth token available - cannot fetch funnels');
      setFetchError("Authentication required. Please refresh the page.");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setFetchError(null);
    try {
      console.log('[LP Builder] Fetching funnels...');
      const res = await api.get("/funnels");
      const data = res.data;
      const list = Array.isArray(data) ? data : data.funnels || [];
      console.log('[LP Builder] Funnels loaded successfully:', list.length);
      setFunnels(list);
    } catch (e) {
      console.error('[LP Builder] Error fetching funnels:', e.response?.status, e.response?.data);
      setFetchError(e.response?.data?.detail || e.message || "Failed to load funnels");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Delay funnel fetch slightly to ensure auth token is loaded
    const token = getToken();
    console.log('[LP Builder] Mount useEffect - token exists:', !!token);
    if (token) {
      console.log('[LP Builder] Token found, fetching funnels immediately');
      fetchFunnels();
    } else {
      console.log('[LP Builder] No token on mount, will retry after 100ms');
      // If no token on mount, wait 100ms and try again (allows localStorage to load)
      const timer = setTimeout(() => {
        const retryToken = getToken();
        console.log('[LP Builder] Retry after 100ms - token exists:', !!retryToken);
        if (retryToken) {
          fetchFunnels();
        } else {
          console.error('[LP Builder] Still no token after retry - auth failed');
          setFetchError("Authentication required. Please refresh the page.");
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [fetchFunnels]);

  const selectedFunnelId = sectionData?.funnel_id || "";
  const embeddable = useMemo(() => funnels.filter((f) => f.embed_key), [funnels]);
  const unpublishedCount = funnels.length - embeddable.length;

  const handleSelect = (funnelId) => {
    const funnel = funnels.find((f) => f.id === funnelId);
    if (!funnel) return;
    onFieldChange(sectionId, {
      ...sectionData,
      funnel_id: funnel.id,
      embed_key: funnel.embed_key || "",
      funnel_name: funnel.name || "Funnel",
    });
  };

  return (
    <div className="space-y-1.5" data-testid="funnel-selector-editor">
      <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Select Funnel</Label>
      {loading ? (
        <div className="flex items-center gap-2 h-8 px-2 text-xs text-slate-500">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading funnels...
        </div>
      ) : fetchError ? (
        <div className="p-2.5 rounded-md bg-red-500/10 border border-red-500/20" data-testid="funnel-selector-error">
          <p className="text-xs text-red-400">{fetchError}</p>
          <button type="button" onClick={fetchFunnels} className="text-[10px] text-red-300 underline mt-1 hover:text-red-200">Retry</button>
        </div>
      ) : embeddable.length === 0 ? (
        <div className="p-2.5 rounded-md bg-slate-800/60 border border-white/[0.06]" data-testid="funnel-selector-empty">
          {funnels.length === 0 ? (
            <p className="text-xs text-slate-500">No funnels found for this tenant. Create a funnel first, then publish it to generate an embed key.</p>
          ) : (
            <div className="space-y-1">
              <p className="text-xs text-amber-400">{unpublishedCount} funnel{unpublishedCount !== 1 ? "s" : ""} found, but none have been published yet.</p>
              <p className="text-[10px] text-slate-500">Go to the Funnel Builder and click Publish on a funnel to generate its embed key.</p>
            </div>
          )}
        </div>
      ) : (
        <Select value={selectedFunnelId} onValueChange={handleSelect} disabled={!isEditable}>
          <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10 text-slate-200" data-testid="funnel-selector-trigger">
            <SelectValue placeholder="Choose a funnel..." />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/10">
            {embeddable.map((f) => (
              <SelectItem key={f.id} value={f.id} className="text-xs text-slate-200">
                {f.name || f.id}
              </SelectItem>
            ))}
            {unpublishedCount > 0 && (
              <div className="px-2 py-1.5 text-[10px] text-slate-600 border-t border-white/5 mt-1">
                {unpublishedCount} funnel{unpublishedCount !== 1 ? "s" : ""} without embed key (publish to enable)
              </div>
            )}
          </SelectContent>
        </Select>
      )}
      {selectedFunnelId && sectionData?.funnel_name && (
        <p className="text-[10px] text-slate-500 flex items-center gap-1">
          <Check className="w-3 h-3 text-emerald-400" /> {sectionData.funnel_name}
        </p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RIGHT PANEL — Section Inspector (matches FunnelPropertiesPanel)
// ═══════════════════════════════════════════════════════════════

function SectionInspector({ sectionId, sectionKey, sectionData, onFieldChange, isSaving, isEditable }) {
  const config = SECTION_CONFIG[sectionKey];
  if (!config) return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="text-center">
        <Layers className="w-8 h-8 text-slate-700 mx-auto mb-2" />
        <p className="text-xs text-slate-500">Select a section to edit</p>
      </div>
    </div>
  );
  const Icon = config.icon;

  // GUARDRAIL (MA-LP-PROD-012): onFieldChange receives sectionId (UUID), not type.
  const updateField = (fieldKey, value) => {
    if (fieldKey === "_items") { onFieldChange(sectionId, value); }
    else { onFieldChange(sectionId, { ...sectionData, [fieldKey]: value }); }
  };

  return (
    <ScrollArea className="flex-1" data-testid={`inspector-${sectionKey}`}>
      <div className="p-5 space-y-4">
        {/* Section header — matches FunnelPropertiesPanel header style */}
        <div className="flex items-center gap-2.5 pb-3 border-b border-white/10">
          <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0">
            <Icon className={cn("h-3.5 w-3.5", config.color)} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-100">{config.label}</h3>
          </div>
          {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />}
        </div>

        {config.fields.map((field) => {
          const val = field.key === "_items" ? sectionData : sectionData?.[field.key];
          if (field.type === "funnel_selector") return <FunnelSelectorEditor key={field.key} sectionId={sectionId} sectionData={sectionData} onFieldChange={onFieldChange} isEditable={isEditable} />;
          if (field.type === "layout_selector") return (
            <div key={field.key} className="space-y-1.5">
              <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label>
              <Select value={val || "centered"} onValueChange={(v) => updateField(field.key, v)} disabled={!isEditable}>
                <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10 text-slate-200" data-testid={`editor-${sectionKey}-${field.key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="centered" className="text-xs text-slate-200">Centered</SelectItem>
                  <SelectItem value="full" className="text-xs text-slate-200">Full Width</SelectItem>
                </SelectContent>
              </Select>
            </div>
          );
          if (field.type === "number") return (
            <div key={field.key} className="space-y-1.5">
              <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label>
              <Input type="number" value={val || ""} onChange={(e) => updateField(field.key, parseInt(e.target.value, 10) || 0)} disabled={!isEditable}
                className="h-8 text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50" data-testid={`editor-${sectionKey}-${field.key}`} />
            </div>
          );
          if (field.type === "trust_signals_list") return <div key={field.key} className="space-y-1.5"><Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label><TrustSignalsEditor value={val} onChange={(v) => updateField(field.key, v)} /></div>;
          if (field.type === "faq_list") return <div key={field.key} className="space-y-1.5"><Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label><FaqEditor value={val} onChange={(v) => updateField(field.key, v)} /></div>;
          if (field.type === "list") return <div key={field.key} className="space-y-1.5"><Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label><ListFieldEditor value={val} onChange={(v) => updateField(field.key, v)} /></div>;
          if (field.type === "steps") return <div key={field.key} className="space-y-1.5"><Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label><StepsFieldEditor value={val} onChange={(v) => updateField(field.key, v)} /></div>;
          if (field.type === "textarea") return (
            <div key={field.key} className="space-y-1.5">
              <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label>
              <Textarea value={val || ""} onChange={(e) => updateField(field.key, e.target.value)} rows={field.rows || 2} maxLength={field.maxLength} disabled={!isEditable}
                className="text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50 resize-y" data-testid={`editor-${sectionKey}-${field.key}`} />
              {field.maxLength && <p className="text-[10px] text-slate-600 text-right">{(val || "").length}/{field.maxLength}</p>}
            </div>
          );
          if (field.type === "toggle") return (
            <div key={field.key} className="flex items-center justify-between gap-3 py-1" data-testid={`editor-${sectionKey}-${field.key}`}>
              <div className="space-y-0.5">
                <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label>
                {field.hint && <p className="text-[10px] text-slate-600 leading-tight">{field.hint}</p>}
              </div>
              <Switch checked={!!val} onCheckedChange={(v) => updateField(field.key, v)} disabled={!isEditable} />
            </div>
          );
          return (
            <div key={field.key} className="space-y-1.5">
              <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{field.label}</Label>
              <Input value={val || ""} onChange={(e) => updateField(field.key, e.target.value)} maxLength={field.maxLength} disabled={!isEditable}
                placeholder={field.placeholder || ""}
                className="h-8 text-xs bg-white/5 border-white/10 text-slate-200 focus:border-indigo-400/50" data-testid={`editor-${sectionKey}-${field.key}`} />
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

// ═══════════════════════════════════════════════════════════════
// PUBLIC URL SECTION — LP-centric route management (MA-DOMAINS-023)
// ═══════════════════════════════════════════════════════════════

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function PublicURLSection({ lpId, lpMetadata, lpStatus, systemSlug, onSystemSlugUpdate }) {
  const [domains, setDomains] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [primaryRouteId, setPrimaryRouteId] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [slug, setSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [copiedSystem, setCopiedSystem] = useState(false);
  const slugCheckTimer = useRef(null);

  // MA-LP-URL-026: System slug editor state
  const [editingSlug, setEditingSlug] = useState(false);
  const [systemSlugInput, setSystemSlugInput] = useState(systemSlug || "");
  const [systemSlugStatus, setSystemSlugStatus] = useState(null);
  const [savingSystemSlug, setSavingSystemSlug] = useState(false);
  const systemSlugTimer = useRef(null);

  // Sync systemSlugInput when parent prop changes
  useEffect(() => {
    if (systemSlug && !editingSlug) {
      setSystemSlugInput(systemSlug);
    }
  }, [systemSlug, editingSlug]);

  // MA-LP-URL-025: Compute system URL
  const appPublicUrl = process.env.REACT_APP_BACKEND_URL || "";
  const currentSystemUrl = systemSlug ? `${appPublicUrl}/p/${systemSlug}` : null;
  const previewSystemUrl = systemSlugInput ? `${appPublicUrl}/p/${systemSlugInput}` : null;

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  }), []);

  // Fetch domains and LP routes on mount
  const fetchData = useCallback(async () => {
    try {
      const [domRes, routeRes] = await Promise.all([
        fetch(`${API_BASE}/api/domains`, { headers }),
        fetch(`${API_BASE}/api/landing-pages/${lpId}/routes`, { headers }),
      ]);
      const domData = await domRes.json();
      const routeData = await routeRes.json();

      const activeDomains = (domData.domains || []).filter(
        d => ["verified", "activation_pending", "active"].includes(d.status)
      );
      setDomains(activeDomains);
      setRoutes(routeData.routes || []);
      setPrimaryRouteId(routeData.primary_route_id);

      // Pre-populate from primary route
      const primary = (routeData.routes || []).find(r => r.id === routeData.primary_route_id);
      if (primary) {
        setSelectedDomain(primary.domain_id);
        setSlug(primary.slug);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [lpId, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live slug validation (debounced)
  useEffect(() => {
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    if (!selectedDomain || slug === null) { setSlugStatus(null); return; }

    // Don't validate if nothing changed from current primary
    const primary = routes.find(r => r.id === primaryRouteId);
    if (primary && primary.domain_id === selectedDomain && primary.slug === slug) {
      setSlugStatus({ available: true, current: true });
      return;
    }

    // Validate format locally first
    if (slug !== "" && !/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(slug)) {
      setSlugStatus({ available: false, reason: "Invalid format. Use lowercase letters, numbers, and hyphens only." });
      return;
    }
    if (slug.includes("--")) {
      setSlugStatus({ available: false, reason: "No consecutive hyphens allowed." });
      return;
    }

    setSlugStatus({ checking: true });
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/domains/${selectedDomain}/slug-available?slug=${encodeURIComponent(slug)}&exclude_lp=${lpId}`,
          { headers }
        );
        const data = await res.json();
        setSlugStatus(data);
      } catch {
        setSlugStatus(null);
      }
    }, 400);

    return () => { if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current); };
  }, [slug, selectedDomain, lpId, headers, routes, primaryRouteId]);

  // MA-LP-URL-026: System slug live validation (debounced)
  useEffect(() => {
    if (systemSlugTimer.current) clearTimeout(systemSlugTimer.current);
    if (!editingSlug) { setSystemSlugStatus(null); return; }
    const val = systemSlugInput.trim();

    // Unchanged from current slug
    if (val === systemSlug) {
      setSystemSlugStatus({ current: true });
      return;
    }
    // Empty
    if (!val) {
      setSystemSlugStatus({ available: false, reason: "Slug cannot be empty." });
      return;
    }
    // Local format validation
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(val)) {
      setSystemSlugStatus({ available: false, reason: "Use only lowercase letters, numbers, and hyphens. Must start and end with a letter or number." });
      return;
    }
    if (val.includes("--")) {
      setSystemSlugStatus({ available: false, reason: "No consecutive hyphens allowed." });
      return;
    }
    if (val.length > 128) {
      setSystemSlugStatus({ available: false, reason: "Maximum 128 characters." });
      return;
    }

    setSystemSlugStatus({ checking: true });
    systemSlugTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/landing-pages/slug-available?slug=${encodeURIComponent(val)}&exclude_lp=${lpId}`,
          { headers }
        );
        const data = await res.json();
        setSystemSlugStatus(data);
      } catch {
        setSystemSlugStatus(null);
      }
    }, 400);

    return () => { if (systemSlugTimer.current) clearTimeout(systemSlugTimer.current); };
  }, [systemSlugInput, editingSlug, systemSlug, lpId, headers]);

  // MA-LP-URL-026: Save system slug
  const handleSaveSystemSlug = async () => {
    const val = systemSlugInput.trim();
    if (val === systemSlug) { setEditingSlug(false); return; }
    setSavingSystemSlug(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/landing-pages/${lpId}`, {
        method: "PATCH", headers,
        body: JSON.stringify({ system_slug: val }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to update slug");
      setEditingSlug(false);
      setSystemSlugStatus(null);
      if (onSystemSlugUpdate) onSystemSlugUpdate(val);
    } catch (e) { setError(e.message); }
    finally { setSavingSystemSlug(false); }
  };

  const canSaveSystemSlug = systemSlugStatus?.available && systemSlugInput.trim() !== systemSlug && !savingSystemSlug;

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/landing-pages/${lpId}/primary-route`, {
        method: "PUT", headers,
        body: JSON.stringify({ domain_id: selectedDomain, slug }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to save");
      setIsEditing(false);
      fetchData();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove this page's public URL?")) return;
    setRemoving(true); setError(null);
    try {
      await fetch(`${API_BASE}/api/landing-pages/${lpId}/primary-route`, { method: "DELETE", headers });
      setSelectedDomain(""); setSlug(""); setIsEditing(false);
      fetchData();
    } catch (e) { setError(e.message); }
    finally { setRemoving(false); }
  };

  if (loading) {
    return (
      <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-sky-400" /><h3 className="text-xs font-semibold text-slate-100">Public URL</h3></div>
        <p className="text-xs text-slate-500">Loading...</p>
      </div>
    );
  }

  if (lpStatus === "archived") {
    return (
      <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-slate-600" /><h3 className="text-xs font-semibold text-slate-400">Public URL</h3></div>
        <p className="text-xs text-slate-500">Archived pages are not accessible via any public URL.</p>
      </div>
    );
  }

  const primaryRoute = routes.find(r => r.id === primaryRouteId);
  const secondaryRoutes = routes.filter(r => r.id !== primaryRouteId);
  const hasPrimary = !!primaryRoute;
  const selectedDomainObj = domains.find(d => d.id === selectedDomain);
  const domainName = selectedDomainObj?.domain || "";
  const previewUrl = domainName ? `https://${domainName}${slug ? `/${slug}` : "/"}` : "";
  const canSave = selectedDomain && (slugStatus?.available || slugStatus?.current) && !saving;

  // Warning: primary route was removed externally
  const primaryGone = primaryRouteId && !primaryRoute && !loading;

  return (
    <div data-testid="public-url-section" className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-sky-400" />
          <h3 className="text-xs font-semibold text-slate-100">Public URL</h3>
        </div>
        {hasPrimary && !isEditing && (
          <button data-testid="edit-url-btn" onClick={() => setIsEditing(true)}
            className="text-[10px] text-sky-400 hover:text-sky-300 transition-colors">Edit</button>
        )}
      </div>

      {/* MA-LP-URL-026: System URL — editable slug editor */}
      {(currentSystemUrl || systemSlugInput) && (
        <div data-testid="system-url-section" className="px-3 py-2.5 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/15">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Link2 className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] text-emerald-400 font-medium">{hasPrimary ? "Platform URL" : "System URL"}</span>
              {!hasPrimary && !editingSlug && <span className="text-[9px] text-emerald-500/60 font-medium uppercase tracking-wider">Default</span>}
            </div>
            {!editingSlug && (
              <button data-testid="edit-system-slug-btn" onClick={() => setEditingSlug(true)}
                className="text-[10px] text-emerald-400 hover:text-emerald-300 transition-colors">Edit</button>
            )}
          </div>

          {/* View mode */}
          {!editingSlug && (
            <div className="flex items-center gap-2">
              <a href={currentSystemUrl} target="_blank" rel="noopener noreferrer" data-testid="system-url-link"
                className="text-xs font-mono text-emerald-300 hover:text-emerald-200 hover:underline transition-colors break-all flex-1">
                {currentSystemUrl}
              </a>
              <button data-testid="copy-system-url-btn"
                onClick={() => { navigator.clipboard.writeText(currentSystemUrl); setCopiedSystem(true); setTimeout(() => setCopiedSystem(false), 2000); }}
                className="p-1 rounded text-emerald-500 hover:text-emerald-300 transition-colors flex-shrink-0">
                {copiedSystem ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
          )}

          {/* Edit mode */}
          {editingSlug && (
            <div className="space-y-2.5">
              {/* Inline slug input with URL prefix */}
              <div className="flex items-center gap-0">
                <span className="h-8 flex items-center px-2.5 rounded-l-md bg-emerald-500/[0.08] border border-r-0 border-emerald-500/15 text-[10px] text-emerald-500/70 font-mono whitespace-nowrap">
                  {appPublicUrl}/p/
                </span>
                <Input data-testid="system-slug-input"
                  value={systemSlugInput}
                  onChange={e => setSystemSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  placeholder="your-page-slug"
                  maxLength={128}
                  autoFocus
                  className="h-8 text-xs bg-emerald-500/[0.03] border-emerald-500/15 text-emerald-200 font-mono rounded-l-none focus:border-emerald-500/30 focus:ring-emerald-500/20"
                />
              </div>

              {/* Availability indicator */}
              {systemSlugStatus && (
                <div className="flex items-center gap-1.5">
                  {systemSlugStatus.checking && (
                    <span data-testid="system-slug-checking" className="text-[10px] text-slate-500 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />Checking...
                    </span>
                  )}
                  {systemSlugStatus.available && (
                    <span data-testid="system-slug-available" className="text-[10px] text-emerald-400 flex items-center gap-1">
                      <Check className="w-3 h-3" />Available
                    </span>
                  )}
                  {systemSlugStatus.current && (
                    <span className="text-[10px] text-slate-500">Current slug (no change)</span>
                  )}
                  {systemSlugStatus.available === false && !systemSlugStatus.checking && (
                    <span data-testid="system-slug-unavailable" className="text-[10px] text-red-400 flex items-center gap-1">
                      <X className="w-3 h-3" />{systemSlugStatus.reason || "Already in use"}
                    </span>
                  )}
                </div>
              )}

              {/* Warning — always visible when editing */}
              <div data-testid="system-slug-warning" className="flex items-start gap-2 px-2.5 py-2 rounded-md bg-amber-500/[0.06] border border-amber-500/15">
                <LucideAlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber-300 leading-relaxed">
                  Changing this will update your public URL. Old links will no longer work.
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button data-testid="save-system-slug-btn" size="sm" onClick={handleSaveSystemSlug} disabled={!canSaveSystemSlug}
                  className="h-7 px-4 text-xs bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40">
                  {savingSystemSlug ? "Saving..." : "Update Slug"}
                </Button>
                <Button variant="ghost" size="sm" data-testid="cancel-system-slug-btn"
                  onClick={() => { setEditingSlug(false); setSystemSlugInput(systemSlug || ""); setSystemSlugStatus(null); setError(null); }}
                  className="h-7 px-3 text-xs text-slate-400 hover:text-slate-300">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div data-testid="url-error" className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs flex items-center gap-2">
          <LucideAlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{error}
        </div>
      )}

      {/* Warning: route removed externally */}
      {primaryGone && (
        <div data-testid="url-removed-warning" className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center gap-2">
          <LucideAlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          This page is no longer published at a custom domain. Configure a new URL below.
        </div>
      )}

      {/* STATE: Has primary route, not editing */}
      {hasPrimary && !isEditing && (
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Globe className="w-3 h-3 text-sky-400" />
            <span className="text-[10px] text-sky-400 font-medium">Custom Domain URL</span>
            <span className="text-[9px] text-sky-500/60 font-medium uppercase tracking-wider">Primary</span>
          </div>
          <div className="flex items-center gap-2">
            <a href={primaryRoute.url} target="_blank" rel="noopener noreferrer" data-testid="primary-url-link"
              className="text-sm font-mono text-sky-300 hover:text-sky-200 hover:underline transition-colors break-all">
              {primaryRoute.url}
            </a>
            <a href={primaryRoute.url} target="_blank" rel="noopener noreferrer"
              className="p-1 rounded text-slate-500 hover:text-sky-300 transition-colors flex-shrink-0">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="flex items-center gap-2">
            {primaryRoute.domain_status === "active" ? (
              <span data-testid="url-status-live" className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live
              </span>
            ) : (
              <span data-testid="url-status-inactive" className="inline-flex items-center gap-1 text-[10px] text-amber-400 font-medium">
                <LucideAlertCircle className="w-3 h-3" />Domain inactive
              </span>
            )}
          </div>
        </div>
      )}

      {/* STATE: No primary / editing / route removed */}
      {(!hasPrimary || isEditing || primaryGone) && (
        <div className="space-y-3">
          {!hasPrimary && !primaryGone && (
            <p className="text-xs text-slate-500">{currentSystemUrl ? "Optionally connect a custom domain for branded URLs." : "Connect this page to a custom domain URL."}</p>
          )}

          {/* Domain dropdown */}
          {domains.length === 0 ? (
            <div data-testid="no-domains-cta" className="px-3 py-3 rounded-lg bg-slate-800/40 border border-white/[0.06]">
              <p className="text-xs text-slate-400">No active domains available.</p>
              <a href="/app/settings/domains" className="text-[10px] text-sky-400 hover:underline mt-1 inline-block">
                Connect a domain in Settings
              </a>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-[10px] text-slate-400">Domain</Label>
                <Select value={selectedDomain} onValueChange={v => { setSelectedDomain(v); setSlugStatus(null); }}>
                  <SelectTrigger data-testid="domain-dropdown" className="h-8 text-xs bg-white/5 border-white/10 text-slate-200">
                    <SelectValue placeholder="Select domain" />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        <span className="font-mono text-xs">{d.domain}</span>
                        {d.status !== "active" && (
                          <span className="ml-2 text-[9px] text-amber-400">({d.status})</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Slug input */}
              <div className="space-y-1.5">
                <Label className="text-[10px] text-slate-400">Path</Label>
                <div className="flex items-center gap-0">
                  <span className="h-8 flex items-center px-3 rounded-l-md bg-white/[0.08] border border-r-0 border-white/10 text-[10px] text-slate-400 font-mono whitespace-nowrap">
                    {domainName || "domain"}/
                  </span>
                  <Input data-testid="slug-input"
                    value={slug}
                    onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    placeholder="offer-1 (empty = homepage)"
                    maxLength={128}
                    className="h-8 text-xs bg-white/5 border-white/10 text-slate-200 font-mono rounded-l-none"
                  />
                </div>
                {/* Slug status */}
                {slugStatus && (
                  <div className="flex items-center gap-1.5 mt-1">
                    {slugStatus.checking && <span className="text-[10px] text-slate-500">Checking...</span>}
                    {slugStatus.available && !slugStatus.current && (
                      <span data-testid="slug-available" className="text-[10px] text-emerald-400 flex items-center gap-1">
                        <Check className="w-3 h-3" />Available
                      </span>
                    )}
                    {slugStatus.current && (
                      <span className="text-[10px] text-slate-500">Current path</span>
                    )}
                    {slugStatus.available === false && (
                      <span data-testid="slug-unavailable" className="text-[10px] text-red-400 flex items-center gap-1">
                        <LucideAlertCircle className="w-3 h-3" />{slugStatus.reason}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* URL Preview */}
              {previewUrl && selectedDomain && (
                <div data-testid="url-preview" className="px-3 py-2 rounded-lg bg-sky-500/5 border border-sky-500/10">
                  <p className="text-[10px] text-sky-400/70 mb-0.5">Preview</p>
                  <p className="text-xs font-mono text-sky-300 break-all">{previewUrl}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-1">
                <Button data-testid="save-url-btn" size="sm" onClick={handleSave} disabled={!canSave}
                  className="h-7 px-4 text-xs bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-40">
                  {saving ? "Saving..." : hasPrimary ? "Update URL" : "Save URL"}
                </Button>
                {isEditing && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    setIsEditing(false);
                    if (primaryRoute) { setSelectedDomain(primaryRoute.domain_id); setSlug(primaryRoute.slug); }
                  }} className="h-7 px-3 text-xs text-slate-400">
                    Cancel
                  </Button>
                )}
                {hasPrimary && (
                  <Button data-testid="remove-url-btn" variant="ghost" size="sm" onClick={handleRemove} disabled={removing}
                    className="h-7 px-3 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 ml-auto">
                    {removing ? "Removing..." : "Remove"}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Secondary routes */}
      {secondaryRoutes.length > 0 && (
        <div data-testid="secondary-routes" className="pt-2 border-t border-white/[0.06] mt-3">
          <p className="text-[10px] text-slate-500 mb-2">Also available at:</p>
          <div className="space-y-1">
            {secondaryRoutes.map(r => (
              <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[11px] font-mono text-slate-400 hover:text-sky-300 transition-colors">
                <Link2 className="w-3 h-3 flex-shrink-0" />{r.url}
              </a>
            ))}
          </div>
          <a href="/app/settings/domains" className="text-[10px] text-slate-600 hover:text-slate-400 mt-2 inline-block transition-colors">
            Manage all routes in Settings
          </a>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// LP SETTINGS VIEW — wired to Settings tab
// ═══════════════════════════════════════════════════════════════

function LPSettingsView({ lp, lpName, setLpName, onRenameSave, isRenaming, activeVersion, onArchive, onSystemSlugUpdate, brandConfig, onThemeChange }) {
  const stylingMode = activeVersion?.styling_mode || 'legacy';
  const customTheme = activeVersion?.custom_theme || {};
  const isLegacy = stylingMode === 'legacy' || !stylingMode;
  const isCustom = stylingMode === 'custom';

  const brandTheme = useMemo(() => {
    if (!brandConfig) return resolveTheme({ preset: 'professional' });
    const preset = brandConfig.default_preset || 'professional';
    const anchor = brandConfig.default_anchor_color || brandConfig.brand_primary || brandConfig.accent;
    const pd = PRESETS[preset] || PRESETS.professional;
    return resolveTheme({ preset, anchor_color: anchor, mode: brandConfig.mode_override || pd.mode });
  }, [brandConfig]);

  const currentTheme = useMemo(() => {
    if (isCustom) return resolveTheme({ preset: customTheme.preset || 'professional', anchor_color: customTheme.anchor_color, mode: customTheme.mode });
    return brandTheme;
  }, [isCustom, customTheme, brandTheme]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6" data-testid="lp-settings-view">
      <div className="max-w-xl mx-auto space-y-6">
        <div>
          <h2 className="text-sm font-semibold text-slate-100 mb-4">Landing Page Settings</h2>
        </div>

        {/* Theme Panel */}
        <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="lp-theme-panel">
          <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Theme</h3>

          {/* Mode toggle */}
          <div className="flex gap-2" data-testid="lp-theme-mode-toggle">
            <button
              onClick={() => onThemeChange({ styling_mode: 'inherit_brand', custom_theme: null })}
              className="flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all border"
              style={{
                backgroundColor: !isCustom && !isLegacy ? 'rgba(99,102,241,0.08)' : 'transparent',
                borderColor: !isCustom && !isLegacy ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
                color: !isCustom && !isLegacy ? '#c7d2fe' : '#71717a',
              }}
              data-testid="lp-theme-inherit-btn"
            >
              Use Brand Theme
            </button>
            <button
              onClick={() => {
                const seed = isCustom && customTheme.preset ? customTheme : {
                  preset: brandConfig?.default_preset || 'professional',
                  anchor_color: brandConfig?.default_anchor_color || brandConfig?.brand_primary || brandConfig?.accent || null,
                  mode: brandConfig?.mode_override || null,
                };
                onThemeChange({ styling_mode: 'custom', custom_theme: seed });
              }}
              className="flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all border"
              style={{
                backgroundColor: isCustom ? 'rgba(99,102,241,0.08)' : 'transparent',
                borderColor: isCustom ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
                color: isCustom ? '#c7d2fe' : '#71717a',
              }}
              data-testid="lp-theme-custom-btn"
            >
              Customize
            </button>
          </div>

          {/* Legacy opt-in notice */}
          {isLegacy && (
            <div className="py-2 px-3 rounded-lg bg-amber-500/[0.06] border border-amber-500/20">
              <p className="text-[11px] text-amber-300">Legacy theme — select "Use Brand Theme" to upgrade</p>
            </div>
          )}

          {/* Inherit info */}
          {!isCustom && !isLegacy && (
            <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-white/[0.02] border border-white/[0.06]" data-testid="lp-theme-inherit-info">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: currentTheme.tokens.primary }} />
              <span className="text-xs text-slate-400">{currentTheme.name} ({currentTheme.mode})</span>
            </div>
          )}

          {/* Custom controls */}
          {isCustom && (
            <div className="space-y-3 py-2 px-3 rounded-lg bg-white/[0.02] border border-white/[0.06]" data-testid="lp-theme-custom-controls">
              {/* Preset */}
              <div>
                <label className="text-[11px] text-slate-500 font-medium mb-1.5 block">Preset</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {Object.entries(PRESETS).map(([key, pd]) => (
                    <button key={key} data-testid={`lp-theme-preset-${key}`}
                      onClick={() => onThemeChange({ styling_mode: 'custom', custom_theme: { ...customTheme, preset: key } })}
                      className="py-1.5 px-2 rounded-md text-[11px] font-medium transition-all border"
                      style={{
                        backgroundColor: customTheme.preset === key ? 'rgba(99,102,241,0.1)' : 'transparent',
                        borderColor: customTheme.preset === key ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
                        color: customTheme.preset === key ? '#e0e7ff' : '#71717a',
                      }}
                    >{pd.name}</button>
                  ))}
                </div>
              </div>

              {/* Accent */}
              <div>
                <label className="text-[11px] text-slate-500 font-medium mb-1.5 block">Accent Color</label>
                {brandConfig?.extracted_colors?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[9px] text-slate-600 mb-1">From your logo</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {brandConfig.extracted_colors.map((c, i) => {
                        const hex = c.normalized_hex || c.hex;
                        const sel = (customTheme.anchor_color || '#6366F1') === hex;
                        return (
                          <button key={`logo-${i}`} onClick={() => onThemeChange({ styling_mode: 'custom', custom_theme: { ...customTheme, anchor_color: hex } })}
                            className="w-6 h-6 rounded-md transition-all"
                            style={{ backgroundColor: hex, border: sel ? '2px solid #fff' : '1.5px solid rgba(255,255,255,0.08)', transform: sel ? 'scale(1.1)' : 'scale(1)' }}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                <p className="text-[9px] text-slate-600 mb-1">Preset colors</p>
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { hex: '#6366F1', l: 'Indigo' }, { hex: '#3B82F6', l: 'Blue' }, { hex: '#14B8A6', l: 'Teal' }, { hex: '#10B981', l: 'Emerald' },
                    { hex: '#8B5CF6', l: 'Purple' }, { hex: '#F97316', l: 'Orange' }, { hex: '#F59E0B', l: 'Amber' }, { hex: '#EC4899', l: 'Pink' },
                  ].map(({ hex, l }) => {
                    const sel = (customTheme.anchor_color || '#6366F1') === hex;
                    return (
                      <button key={hex} title={l} onClick={() => onThemeChange({ styling_mode: 'custom', custom_theme: { ...customTheme, anchor_color: hex } })}
                        className="w-6 h-6 rounded-md transition-all"
                        style={{ backgroundColor: hex, border: sel ? '2px solid #fff' : '1.5px solid rgba(255,255,255,0.08)', transform: sel ? 'scale(1.1)' : 'scale(1)' }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Mode */}
              <div>
                <label className="text-[11px] text-slate-500 font-medium mb-1.5 block">Mode</label>
                <div className="flex gap-1 bg-white/[0.04] rounded-lg p-0.5 border border-white/[0.06]">
                  {[{ v: null, l: 'Auto' }, { v: 'light', l: 'Light' }, { v: 'dark', l: 'Dark' }].map(({ v, l }) => {
                    const on = (customTheme.mode ?? null) === v;
                    return (
                      <button key={l} onClick={() => onThemeChange({ styling_mode: 'custom', custom_theme: { ...customTheme, mode: v } })}
                        className="flex-1 py-1 rounded-md text-[11px] font-medium transition-colors"
                        style={{ backgroundColor: on ? 'rgba(255,255,255,0.08)' : 'transparent', color: on ? '#e4e4e7' : '#71717a' }}>
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 rounded-xl border border-white/10 bg-white/[0.03] p-5">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-300">Name</Label>
            <Input
              value={lpName}
              onChange={(e) => setLpName(e.target.value)}
              onBlur={onRenameSave}
              onKeyDown={(e) => e.key === "Enter" && onRenameSave()}
              disabled={isRenaming}
              className="h-9 text-sm bg-white/5 border-white/10 text-slate-100"
              data-testid="settings-lp-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-300">Status</Label>
            <div className="flex items-center gap-2">
              <Badge className={cn(
                "text-xs border-0 px-2 py-0.5",
                lp?.status === "active" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"
              )}>{lp?.status || "draft"}</Badge>
              {activeVersion && (
                <span className="text-xs text-slate-500">
                  v{activeVersion.version_number} ({activeVersion.status})
                </span>
              )}
            </div>
          </div>

          {lp?.primary_use_case && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-300">Primary Use Case</Label>
              <p className="text-sm text-slate-200">{lp.primary_use_case}</p>
            </div>
          )}

          {lp?.created_at && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-300">Created</Label>
              <p className="text-sm text-slate-400">{new Date(lp.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            </div>
          )}
        </div>

        {/* Public URL Section — MA-DOMAINS-023 + MA-LP-URL-025 + MA-LP-URL-026 */}
        {lp?.id && (
          <PublicURLSection lpId={lp.id} lpMetadata={lp?.metadata} lpStatus={lp?.status} systemSlug={lp?.system_slug}
            onSystemSlugUpdate={onSystemSlugUpdate} />
        )}

        {/* Archive — Danger Zone */}
        <div className="space-y-3 rounded-xl border border-red-500/20 bg-red-500/[0.03] p-5" data-testid="settings-danger-zone">
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-red-400" />
            <h3 className="text-xs font-semibold text-red-300">Danger Zone</h3>
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Archiving hides this landing page from all listings and deactivates its published version.
            Version history and data are preserved but the page will no longer be served.
          </p>
          <Button
            variant="outline" size="sm"
            className="h-8 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
            onClick={onArchive}
            data-testid="settings-archive-btn"
          >
            <Archive className="h-3 w-3 mr-1.5" />Archive Landing Page
          </Button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// VERSION HISTORY VIEW — wired to History tab
// ═══════════════════════════════════════════════════════════════

function VersionHistoryView({ versions, activeVersion, onSwitchVersion }) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6" data-testid="lp-history-view">
      <div className="max-w-xl mx-auto space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <History className="h-4 w-4 text-indigo-400" />
          <h2 className="text-sm font-semibold text-slate-100">Version History</h2>
          <span className="text-xs text-slate-500">({versions.length})</span>
        </div>

        <div className="space-y-2">
          {versions.map((v) => {
            const isCurrent = v.id === activeVersion?.id;
            return (
              <button
                key={v.id}
                onClick={() => onSwitchVersion(v.id)}
                className={cn(
                  "w-full text-left p-4 rounded-xl border transition-colors duration-150",
                  isCurrent
                    ? "bg-indigo-500/10 border-indigo-400/30"
                    : "bg-white/[0.03] border-white/10 hover:bg-white/[0.05]"
                )}
                data-testid={`history-version-${v.version_number}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-sm font-medium text-slate-200">
                      v{v.version_number}
                    </span>
                    <Badge className={cn(
                      "text-[9px] px-1.5 border-0",
                      v.status === "draft" ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"
                    )}>{v.status}</Badge>
                    {isCurrent && (
                      <span className="text-[9px] text-indigo-400 font-medium">current</span>
                    )}
                  </div>
                  {v.created_at && (
                    <span className="text-[10px] text-slate-500">
                      {new Date(v.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
                {v.version_label && (
                  <p className="text-xs text-slate-400 mt-1.5 pl-5">{v.version_label}</p>
                )}
                {v.change_summary && (
                  <p className="text-[11px] text-slate-500 mt-1 pl-5">{v.change_summary}</p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMING SOON PLACEHOLDER
// ═══════════════════════════════════════════════════════════════

function ComingSoonView({ tabName, icon: Icon }) {
  return (
    <div className="flex-1 min-h-0 flex items-center justify-center" data-testid={`${tabName}-coming-soon`}>
      <div className="text-center">
        <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center mx-auto mb-3">
          <Icon className="w-5 h-5 text-slate-600" />
        </div>
        <p className="text-sm text-slate-400 font-medium">{tabName}</p>
        <p className="text-xs text-slate-600 mt-1">Coming soon</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// EMBED TAB — Real embed code for published LPs (MA-LP-PROD-012)
// ═══════════════════════════════════════════════════════════════

function EmbedTabView({ versions, lp }) {
  const [copiedField, setCopiedField] = useState(null);
  const [publicUrl, setPublicUrl] = useState(null);

  const publishedVersion = versions.find((v) => v.is_active) || versions.find((v) => v.status === "published");

  // MA-LP-URL-025: Compute system URL from LP entity
  const appPublicUrl = process.env.REACT_APP_BACKEND_URL || "";
  const systemUrl = lp?.system_slug ? `${appPublicUrl}/p/${lp.system_slug}` : null;

  // Fetch primary public URL (custom domain)
  useEffect(() => {
    if (!lp?.id) return;
    const headers = { Authorization: `Bearer ${getToken()}` };
    fetch(`${API_BASE}/api/landing-pages/${lp.id}/routes`, { headers })
      .then(r => r.json())
      .then(data => {
        const primary = (data.routes || []).find(r => r.id === data.primary_route_id);
        setPublicUrl(primary?.url || null);
      })
      .catch(() => {});
  }, [lp?.id]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!publishedVersion) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center" data-testid="embed-no-published">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center mx-auto mb-3">
            <Code2 className="w-5 h-5 text-slate-600" />
          </div>
          <p className="text-sm text-slate-300 font-medium">No Published Version</p>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">
            Publish a version first to generate embed code. Only published landing pages can be embedded on external sites.
          </p>
        </div>
      </div>
    );
  }

  const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
  const serveUrl = `${baseUrl}/api/landing-pages/serve/${publishedVersion.route_path}`;
  const iframeCode = `<iframe src="${serveUrl}" width="100%" height="800" frameborder="0" style="border:none;width:100%;min-height:800px;" loading="lazy" title="Landing Page"></iframe>`;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6" data-testid="embed-tab-view">
      <div className="max-w-xl mx-auto space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <Code2 className="h-4 w-4 text-indigo-400" />
          <h2 className="text-sm font-semibold text-slate-100">Embed Landing Page</h2>
        </div>

        {/* Custom Domain URL — shown prominently when available */}
        {publicUrl && (
          <div data-testid="embed-public-url" className="space-y-2 rounded-xl border border-sky-500/20 bg-sky-500/[0.04] p-5">
            <div className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5 text-sky-400" />
              <Label className="text-xs text-sky-300 font-medium">Custom Domain URL</Label>
              <span className="text-[9px] text-sky-500/70 font-medium uppercase tracking-wider">Primary</span>
            </div>
            <p className="text-[10px] text-slate-500">Branded public URL — share this with your audience.</p>
            <div className="flex gap-2 items-center">
              <Input
                value={publicUrl}
                readOnly
                className="h-8 text-xs bg-sky-500/5 border-sky-500/15 text-sky-200 font-mono flex-1"
                data-testid="embed-public-url-input"
              />
              <Button
                variant="outline" size="sm"
                className="h-8 px-3 text-xs border-sky-500/20 text-sky-300 hover:bg-sky-500/10"
                onClick={() => copyToClipboard(publicUrl, "public")}
                data-testid="embed-copy-public-btn"
              >
                {copiedField === "public" ? <Check className="h-3 w-3 mr-1 text-emerald-400" /> : <Copy className="h-3 w-3 mr-1" />}
                {copiedField === "public" ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        )}

        {/* MA-LP-URL-025: System URL — always shown */}
        {systemUrl && (
          <div data-testid="embed-system-url" className="space-y-2 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
            <div className="flex items-center gap-2">
              <Link2 className="h-3.5 w-3.5 text-emerald-400" />
              <Label className="text-xs text-emerald-300 font-medium">{publicUrl ? "Platform URL" : "System URL"}</Label>
              {!publicUrl && <span className="text-[9px] text-emerald-500/70 font-medium uppercase tracking-wider">Recommended</span>}
            </div>
            <p className="text-[10px] text-slate-500">{publicUrl ? "Permanent platform-hosted URL (always available)." : "Share this live URL with your audience."}</p>
            <div className="flex gap-2 items-center">
              <Input
                value={systemUrl}
                readOnly
                className="h-8 text-xs bg-emerald-500/5 border-emerald-500/15 text-emerald-200 font-mono flex-1"
                data-testid="embed-system-url-input"
              />
              <Button
                variant="outline" size="sm"
                className="h-8 px-3 text-xs border-emerald-500/20 text-emerald-300 hover:bg-emerald-500/10"
                onClick={() => copyToClipboard(systemUrl, "system")}
                data-testid="embed-copy-system-btn"
              >
                {copiedField === "system" ? <Check className="h-3 w-3 mr-1 text-emerald-400" /> : <Copy className="h-3 w-3 mr-1" />}
                {copiedField === "system" ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        )}

        {/* Internal URL */}
        <div className="space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="embed-direct-url-section">
          <Label className="text-xs text-slate-300 font-medium">Internal Platform URL</Label>
          <p className="text-[10px] text-slate-500">Internal preview/testing URL (not for public sharing).</p>
          <div className="flex gap-2 items-center">
            <Input
              value={serveUrl}
              readOnly
              className="h-8 text-xs bg-white/5 border-white/10 text-slate-300 font-mono flex-1"
              data-testid="embed-direct-url-input"
            />
            <Button
              variant="outline" size="sm"
              className="h-8 px-3 text-xs border-white/10"
              onClick={() => copyToClipboard(serveUrl, "url")}
              data-testid="embed-copy-url-btn"
            >
              {copiedField === "url" ? <Check className="h-3 w-3 mr-1 text-emerald-400" /> : <Copy className="h-3 w-3 mr-1" />}
              {copiedField === "url" ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        {/* iframe Embed Code */}
        <div className="space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="embed-iframe-section">
          <Label className="text-xs text-slate-300 font-medium">iframe Embed</Label>
          <p className="text-[10px] text-slate-500">Embed the full landing page on any website using an iframe.</p>
          <div className="relative">
            <pre className="p-3 rounded-lg bg-black/30 border border-white/[0.06] text-[11px] text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-iframe-code">
              {iframeCode}
            </pre>
            <Button
              variant="ghost" size="sm"
              className="absolute top-2 right-2 h-6 px-2 text-[10px] text-slate-400 hover:text-white"
              onClick={() => copyToClipboard(iframeCode, "iframe")}
              data-testid="embed-copy-iframe-btn"
            >
              {copiedField === "iframe" ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
            </Button>
          </div>
        </div>

        {/* Version context */}
        <div className="rounded-lg bg-indigo-500/5 border border-indigo-500/10 p-3" data-testid="embed-version-info">
          <p className="text-[10px] text-indigo-300 font-medium">Published Version</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            v{publishedVersion.version_number} &mdash; route: <span className="font-mono text-slate-400">{publishedVersion.route_path}</span>
          </p>
        </div>

        {/* Note about funnel embeds */}
        <div className="rounded-lg bg-slate-800/40 border border-white/[0.06] p-3" data-testid="embed-funnel-note">
          <p className="text-[10px] text-slate-400">
            <span className="font-medium text-slate-300">Note:</span> If this landing page includes a funnel embed section, the funnel widget is automatically included in the served page. No separate funnel embed code is needed.
          </p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function LandingPageBuilder() {
  const { landing_page_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromProfitPath = searchParams.get("from_profit_path");

  // Enter editor mode (hides OS sidebar/header) — same as FunnelDetailsPage
  useEditorMode(true);

  // ── State ──
  const [lp, setLp] = useState(null);
  const [lpName, setLpName] = useState("");
  const [versions, setVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [brandConfig, setBrandConfig] = useState(null);
  // GUARDRAIL: sections[] is the only active source of truth for builder logic
  const [sections, setSections] = useState([]);
  // GUARDRAIL: selection uses section.id (permanent identity), not type or index
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const sectionsRef = useRef([]);
  const activeVersionRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [publishing, setPublishing] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  // MA-UI-ALIGN-062: In-canvas visual mode (builder = full editing chrome, preview = soft page flow)
  const [canvasMode, setCanvasMode] = useState("builder");
  const [isDirty, setIsDirty] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [activeTab, setActiveTab] = useState("builder");
  const [isRenaming, setIsRenaming] = useState(false);
  const saveTimerRef = useRef(null);

  // ── Load LP entity + versions ──
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [lpRes, versionsRes, brandRes] = await Promise.all([
        api.get(`/landing-pages/${landing_page_id}`),
        api.get(`/landing-pages/${landing_page_id}/versions`),
        api.get('/brand-config').catch(() => ({ data: null })),
      ]);

      // MA-LP-THEME-036 + MA-THEME-RENDER-STEP2: Theme resolution
      // When LP version has styling_mode (not legacy), use theme engine bridge.
      // Otherwise, use legacy extractLPTheme path.
      const lpData = lpRes.data;
      const brandData = brandRes?.data;

      // Store brand data for later theme resolution after version is identified
      const _brandData = brandData;
      setBrandConfig(brandData);

      setLp(lpData);
      setLpName(lpRes.data.name || "");
      const allVersions = versionsRes.data.versions || [];
      setVersions(allVersions);
      const draft = allVersions.find((v) => v.status === "draft");
      const active = allVersions.find((v) => v.is_active);
      const target = draft || active || allVersions[0];

      // Resolve theme based on version's styling_mode
      const versionDoc = target || {};
      const canonicalTheme = resolveLpTheme(
        {
          final_theme: versionDoc.final_theme,
          styling_mode: versionDoc.styling_mode,
          custom_theme: versionDoc.custom_theme,
        },
        _brandData
      );
      if (canonicalTheme) {
        // Theme engine path — bridge tokens (identical to runtime)
        setActiveBrand(canonicalTheme);
      } else {
        // Legacy path
        const legacyTheme = extractLPTheme({ lp: lpData, apiBrandConfig: _brandData });
        setActiveBrand(legacyTheme);
      }

      if (target) {
        const verRes = await api.get(`/landing-pages/versions/${target.id}`);
        setActiveVersion(verRes.data);
        
        const fullVersionTheme = resolveLpTheme(
          {
            final_theme: verRes.data.final_theme,
            styling_mode: verRes.data.styling_mode,
            custom_theme: verRes.data.custom_theme,
          },
          _brandData
        );
        
        // MA-156: Structured debug output for LP Builder
        const MA_DEBUG_THEME = {
          surface: "lp_builder",
          lp_id: lpData.id,
          version_id: verRes.data.id,
          styling_mode: verRes.data.styling_mode,
          has_custom_theme: !!verRes.data.custom_theme,
          theme_source: verRes.data.custom_theme ? "lp_version_custom" : (_brandData ? "brand_config" : "none"),
          fullVersionTheme_present: !!fullVersionTheme,
          timestamp: new Date().toISOString()
        };
        console.log('═══════════════════════════════════════════');
        console.log('MA_DEBUG_THEME (LP Builder):');
        console.log(JSON.stringify(MA_DEBUG_THEME, null, 2));
        console.log('═══════════════════════════════════════════');
        
        if (fullVersionTheme) {
          setActiveBrand(fullVersionTheme);
        }
        
        const built = toBuilderSections(verRes.data.snapshot_json || {});
        setSections(built);
        sectionsRef.current = built;
        if (built.length > 0) setSelectedSectionId(built[0].id);
      }
    } catch (e) {
      toast({ title: "Failed to load", description: e.response?.data?.detail || "Landing page not found", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [landing_page_id]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { activeVersionRef.current = activeVersion; }, [activeVersion]);

  const handleCreateDraft = async () => {
    if (!activeVersion || creatingDraft) return;
    setCreatingDraft(true);
    try {
      const res = await api.post(`/landing-pages/${landing_page_id}/versions`, {
        version_label: `Draft from v${activeVersion.version_number}`,
        change_summary: `Created draft from ${activeVersion.status} v${activeVersion.version_number}`,
        snapshot_json: { sections: getPopulatedSections(sectionsRef.current) },
      });
      setActiveVersion(res.data);
      const built = toBuilderSections(res.data.snapshot_json || {});
      setSections(built);
      sectionsRef.current = built;
      if (built.length > 0) setSelectedSectionId(built[0].id);
      setVersions((prev) => [res.data, ...prev]);
      toast({ title: "Draft Created", description: `Draft v${res.data.version_number} ready for editing.` });
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail || "Could not create draft", variant: "destructive" });
    } finally {
      setCreatingDraft(false);
    }
  };

  const isEditable = activeVersion?.status === "draft";

  // MA-THEME-PHASE6: Theme change handler — updates version + refreshes preview
  const handleThemeChange = useCallback(async ({ styling_mode, custom_theme }) => {
    if (!activeVersion?.id) return;
    try {
      await api.put(`/landing-pages/versions/${activeVersion.id}`, {
        styling_mode,
        custom_theme: styling_mode === 'custom' ? custom_theme : null,
      });
      const updatedVersion = { ...activeVersion, styling_mode, custom_theme: styling_mode === 'custom' ? custom_theme : null };
      setActiveVersion(updatedVersion);
      // Update preview through canonical bridge
      const theme = resolveLpTheme(
        { styling_mode, custom_theme: styling_mode === 'custom' ? custom_theme : null },
        brandConfig
      );
      if (theme) {
        setActiveBrand(theme);
      } else {
        const legacy = extractLPTheme({ lp, apiBrandConfig: brandConfig });
        setActiveBrand(legacy);
      }
      setSections([...sectionsRef.current]); // force re-render
    } catch (e) {
      toast({ title: "Failed", description: "Could not update theme", variant: "destructive" });
    }
  }, [activeVersion, brandConfig, lp, toast]);

  // GUARDRAIL: derive selected section from ID lookup (not type or index)
  const selectedSec = useMemo(() =>
    sections.find((s) => s.id === selectedSectionId) || sections[0] || null,
    [sections, selectedSectionId]
  );
  const selectedSectionType = selectedSec?.type;

  // ── Section editing with auto-save — sections[] is the active model ──
  // GUARDRAIL (MA-LP-PROD-012): Match by section.id (UUID), NOT section.type.
  // This is critical for multi-instance safety (e.g. two faq or two mid_cta sections).
  const handleSectionChange = useCallback((sectionId, newData) => {
    setSections((prev) => {
      const next = prev.map((s) =>
        s.id === sectionId ? { ...s, data: newData } : s
      );
      sectionsRef.current = next;
      return next;
    });
    setIsDirty(true);
    setSaveStatus("idle");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSaveToServer(), 1000);
  }, []);

  const autoSaveToServer = async () => {
    const ver = activeVersionRef.current;
    if (!ver || ver.status !== "draft") return;
    setSaving(true);
    setSaveStatus("saving");
    try {
      await api.patch(`/landing-pages/versions/${ver.id}`, {
        snapshot_json: { sections: getPopulatedSections(sectionsRef.current) },
      });
      setIsDirty(false);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const handleFullSave = async () => {
    if (!activeVersion || !isEditable) return;
    setSaving(true);
    setSaveStatus("saving");
    try {
      await api.patch(`/landing-pages/versions/${activeVersion.id}`, {
        snapshot_json: { sections: getPopulatedSections(sectionsRef.current) },
      });
      setIsDirty(false);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!activeVersion || activeVersion.status !== "draft") return;
    if (isDirty) await handleFullSave();
    setPublishing(true);
    try {
      await api.post(`/landing-pages/versions/${activeVersion.id}/publish`, { change_summary: `Published v${activeVersion.version_number}` });
      toast({ title: "Published", description: `Version ${activeVersion.version_number} is now live.` });
      await loadData();
    } catch (e) {
      toast({ title: "Publish Failed", description: e.response?.data?.detail || "Could not publish", variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  const handleSwitchVersion = async (versionId) => {
    try {
      const res = await api.get(`/landing-pages/versions/${versionId}`);
      setActiveVersion(res.data);
      const built = toBuilderSections(res.data.snapshot_json || {});
      setSections(built);
      sectionsRef.current = built;
      if (built.length > 0) setSelectedSectionId(built[0].id);
      setIsDirty(false);
      setSaveStatus("idle");
      if (activeTab === "history") setActiveTab("builder");
    } catch {
      toast({ title: "Failed", description: "Could not load version", variant: "destructive" });
    }
  };

  const handleRenameSave = async () => {
    if (!lp || lpName === lp.name || !lpName.trim()) return;
    setIsRenaming(true);
    try {
      await api.patch(`/landing-pages/${landing_page_id}`, { name: lpName.trim() });
      setLp((prev) => ({ ...prev, name: lpName.trim() }));
    } catch { /* silent */ }
    finally { setIsRenaming(false); }
  };

  // MA-LP-PROD-012: Archive landing page
  const handleArchive = async () => {
    setArchiving(true);
    try {
      await api.delete(`/landing-pages/${landing_page_id}`);
      toast({ title: "Archived", description: "Landing page has been archived." });
      navigate("/app/landing-pages");
    } catch (e) {
      toast({ title: "Archive Failed", description: e.response?.data?.detail || "Could not archive", variant: "destructive" });
    } finally {
      setArchiving(false);
      setArchiveConfirmOpen(false);
    }
  };

  // MA-LP-PROD-012: Live Preview — open the served LP in a new tab
  const publishedVersion = useMemo(() =>
    versions.find((v) => v.is_active) || versions.find((v) => v.status === "published"),
    [versions]
  );

  // MA-LP-LAYOUT-027C: Live Preview — serve the version the builder is currently editing
  // Uses preview=true&version_id to allow draft versions, ensuring Live Preview
  // matches Builder canvas (not a stale published snapshot)
  const handleLivePreview = () => {
    const versionToPreview = activeVersion || publishedVersion;
    if (!versionToPreview?.route_path) return;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const previewUrl = `${baseUrl}/api/landing-pages/serve/${versionToPreview.route_path}?preview=true&version_id=${versionToPreview.id}`;
    window.open(previewUrl, "_blank");
  };

  // GUARDRAIL: scrollToSection uses section.id for selection (permanent identity)
  const scrollToSection = (sectionId) => {
    setSelectedSectionId(sectionId);
    const sec = sections.find((s) => s.id === sectionId);
    if (sec) {
      const el = document.getElementById(`preview-section-${sec.type}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PHASE 3: SECTION OPERATIONS — add, delete, reorder
  // ═══════════════════════════════════════════════════════════════

  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // DnD sensors with activation distance to avoid accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Derive which types are singleton-blocked (already in sections[])
  const existingSingletons = useMemo(() => {
    const set = new Set();
    for (const s of sections) {
      if (SINGLETON_TYPES.has(s.type)) set.add(s.type);
    }
    return set;
  }, [sections]);

  // Check if a section can be deleted
  const canDeleteSection = useCallback((section) => {
    if (sections.length <= 1) return false;
    if (section.type === "final_cta") {
      const ctaCount = sections.filter((s) => s.type === "final_cta").length;
      if (ctaCount <= 1) return false;
    }
    return true;
  }, [sections]);

  // Add section handler
  const handleAddSection = (type) => {
    const newSection = {
      id: crypto.randomUUID(),
      type,
      data: getDefaultData(type),
    };
    setSections((prev) => {
      const next = [...prev, newSection];
      sectionsRef.current = next;
      return next;
    });
    setSelectedSectionId(newSection.id);
    setAddPickerOpen(false);
    setIsDirty(true);
    setSaveStatus("idle");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSaveToServer(), 1000);
  };

  // Delete section handler — called after confirmation (or directly for non-warned types)
  const confirmDeleteSection = (section) => {
    if (!canDeleteSection(section)) return;
    if (WARN_DELETE_TYPES.has(section.type)) {
      setDeleteTarget(section);
      return;
    }
    executeDelete(section);
  };

  const executeDelete = (section) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === section.id);
      const next = prev.filter((s) => s.id !== section.id);
      sectionsRef.current = next;
      // Update selection: pick next, or previous, or first
      if (selectedSectionId === section.id && next.length > 0) {
        const newIdx = Math.min(idx, next.length - 1);
        setSelectedSectionId(next[newIdx].id);
      }
      return next;
    });
    setDeleteTarget(null);
    setIsDirty(true);
    setSaveStatus("idle");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSaveToServer(), 1000);
  };

  // Drag-and-drop reorder handler
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const oldIdx = prev.findIndex((s) => s.id === active.id);
      const newIdx = prev.findIndex((s) => s.id === over.id);
      const next = arrayMove(prev, oldIdx, newIdx);
      sectionsRef.current = next;
      return next;
    });
    setIsDirty(true);
    setSaveStatus("idle");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSaveToServer(), 1000);
  };

  const sectionStates = useMemo(() => {
    return sections.map((s) => ({
      id: s.id,
      key: s.type,
      hasData: sectionHasData(s.data),
    }));
  }, [sections]);

  const populatedCount = sectionStates.filter((s) => s.hasData).length;

  // ── Loading ──
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="lp-builder-loading">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-400 mr-3" />
        <span className="text-sm text-slate-400">Loading Landing Page Builder...</span>
      </div>
    );
  }

  if (!lp) {
    return (
      <div className="h-full flex flex-col items-center justify-center" data-testid="lp-builder-not-found">
        <AlertTriangle className="w-8 h-8 text-amber-400 mb-3" />
        <h2 className="text-sm font-semibold text-white mb-1">Landing Page Not Found</h2>
        <p className="text-xs text-slate-500 mb-4">The requested landing page does not exist.</p>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="text-xs border-white/10">Go Back</Button>
      </div>
    );
  }

  const verStatus = activeVersion?.status || "draft";

  // Tab definitions — honest: only wire what's functional
  const tabs = [
    { id: "builder", label: "Builder", icon: Wrench, active: true },
    { id: "settings", label: "Settings", icon: Settings, active: true },
    { id: "history", label: "History", icon: History, active: true },
    { id: "analytics", label: "Analytics", icon: BarChart3, active: false },
    { id: "embed", label: "Embed", icon: Code2, active: true },
  ];

  return (
    <div
      className="flex flex-col h-full overflow-hidden bg-white/[0.02]"
      data-testid="lp-builder"
    >
      {/* ══════════════════════════════════════════════════════
          PAGE ACTION BAR — Same component pattern as FunnelDetailsPage
          ══════════════════════════════════════════════════════ */}
      <div
        className="h-12 flex items-center justify-between px-4 mx-3 mt-3 flex-shrink-0 rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl"
        data-testid="page-action-bar"
      >
        {/* Left: Back + Document Name */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0"
                  onClick={() => fromProfitPath ? navigate(`/app/profit-paths/${fromProfitPath}/builder`) : navigate("/app/landing-pages")}
                  data-testid="lp-builder-back">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{fromProfitPath ? "Back to PPB" : "Back to Landing Pages"}</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="flex items-center gap-1.5 min-w-0 max-w-[240px]">
            <Input value={lpName} onChange={(e) => setLpName(e.target.value)}
              disabled={isRenaming}
              className="h-7 bg-transparent text-sm text-slate-100 border-transparent hover:border-white/20 focus:border-indigo-400 min-w-0 px-2"
              placeholder="Landing page name" onBlur={handleRenameSave} onKeyDown={(e) => e.key === "Enter" && handleRenameSave()}
              data-testid="lp-builder-title" />
            {isRenaming && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
          </div>
        </div>

        {/* Center: Tab Selector — Same indigo pill tabs as FunnelDetailsPage */}
        <div className="flex items-center gap-3">
          <div className="inline-flex h-8 items-center gap-0.5 bg-indigo-500/15 rounded-lg p-0.5 ring-1 ring-indigo-400/20">
            {tabs.map((tab) => (
              <TooltipProvider key={tab.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-7 px-3 text-xs rounded-md transition-all",
                        activeTab === tab.id
                          ? "bg-indigo-500/30 text-white shadow-sm"
                          : tab.active
                            ? "text-indigo-200/70 hover:text-white hover:bg-white/5"
                            : "text-indigo-200/30 cursor-default"
                      )}
                      onClick={() => setActiveTab(tab.id)}
                      data-testid={`tab-${tab.id}`}
                    >
                      <tab.icon className="h-3 w-3 mr-1.5" />{tab.label}
                    </Button>
                  </TooltipTrigger>
                  {!tab.active && (
                    <TooltipContent side="bottom" className="text-xs">Coming soon</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>

        {/* Right: Actions — Same as FunnelDetailsPage */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          {/* Save Status */}
          {saveStatus !== "idle" && (
            <div className="flex items-center gap-1 text-xs text-slate-400 mr-1">
              {saveStatus === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
              {saveStatus === "saved" && <Check className="h-3 w-3 text-green-400" />}
              {saveStatus === "error" && <AlertCircle className="h-3 w-3 text-red-400" />}
              <span className={saveStatus === "saved" ? "text-green-400" : saveStatus === "error" ? "text-red-400" : ""}>
                {saveStatus === "saving" ? "Saving..." : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Error" : ""}
              </span>
            </div>
          )}

          {/* Version badge */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors px-2 py-1 rounded border border-white/[0.06] hover:border-white/[0.12]" data-testid="version-selector">
                <GitBranch className="w-3 h-3" />v{activeVersion?.version_number || "?"}
                <Badge className={cn("h-3.5 text-[9px] px-1 border-0",
                  verStatus === "draft" ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"
                )}>{verStatus}</Badge>
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#161820] border-white/[0.08] min-w-[200px]">
              {versions.map((v) => (
                <DropdownMenuItem key={v.id} onClick={() => handleSwitchVersion(v.id)}
                  className={cn("text-xs cursor-pointer", v.id === activeVersion?.id && "bg-indigo-500/10 text-indigo-300")}>
                  <span className="flex-1">v{v.version_number}</span>
                  <Badge className={cn("h-3.5 text-[9px] px-1 border-0 ml-2",
                    v.status === "draft" ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"
                  )}>{v.status}</Badge>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Live Preview — opens served LP in new tab */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline" size="sm"
                  className={cn(
                    "h-7 text-xs border-white/10",
                    publishedVersion ? "text-slate-200 hover:text-white" : "text-slate-500 cursor-not-allowed"
                  )}
                  onClick={handleLivePreview}
                  disabled={!publishedVersion}
                  data-testid="lp-builder-live-preview"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />Live Preview
                </Button>
              </TooltipTrigger>
              {!publishedVersion && (
                <TooltipContent side="bottom" className="text-xs max-w-[200px]">
                  Publish a version first. Only published landing pages can be previewed as a live page.
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          {/* Save */}
          <Button variant="outline" size="sm" className="h-7 text-xs border-white/10"
            onClick={handleFullSave} disabled={saving || !isDirty || !isEditable} data-testid="lp-builder-save">
            {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
          </Button>

          {/* Create Draft (if not editable) */}
          {!isEditable && activeVersion && (
            <Button variant="outline" size="sm" className="h-7 text-xs border-white/10"
              onClick={handleCreateDraft} disabled={creatingDraft} data-testid="lp-builder-create-draft">
              {creatingDraft ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Copy className="h-3 w-3 mr-1" />}Create Draft
            </Button>
          )}

          {/* Publish — Primary CTA, same as FunnelDetailsPage */}
          <Button size="sm"
            className={cn(
              "h-8 px-4 text-xs font-medium",
              "bg-gradient-to-b from-indigo-500 to-indigo-600",
              "shadow-md shadow-indigo-500/25",
              "text-white border-0 rounded-md",
              "hover:from-indigo-400 hover:to-indigo-500",
              "hover:shadow-lg hover:shadow-indigo-500/30",
              "hover:-translate-y-px",
              "active:translate-y-0 active:shadow-sm",
              "transition-all duration-150 ease-out"
            )}
            onClick={handlePublish} disabled={publishing || !isEditable} data-testid="lp-builder-publish">
            {publishing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1.5" />}Publish
          </Button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          TAB CONTENT — flex-1 min-h-0 locks the viewport
          ══════════════════════════════════════════════════════ */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">

        {/* ── BUILDER TAB: 3-Panel workspace ── */}
        {activeTab === "builder" && (
          <div className="flex-1 min-h-0 flex gap-3 px-3 pb-3 pt-2 overflow-hidden" data-testid="builder-workspace">

            {/* ── LEFT CARD: Sections — 320px glass card ── */}
            <div className={cn(
              "w-[320px] min-w-[320px] flex-shrink-0 flex flex-col overflow-hidden",
              "rounded-2xl border border-white/[0.08]",
              "bg-gradient-to-b from-white/[0.04] to-white/[0.02]",
              "backdrop-blur-xl"
            )} data-testid="lp-builder-left-panel">
              {/* Panel header — matches FunnelStepsPanel header exactly */}
              <div className="flex-shrink-0 px-3 py-[11px] border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-indigo-400" />
                    <h3 className="text-sm font-medium text-slate-200">Sections</h3>
                    <span className="text-xs text-slate-500">({populatedCount}/{sections.length})</span>
                  </div>
                </div>
              </div>

              {/* Section list — Phase 3: DnD sortable + add/delete */}
              <div className="flex-1 overflow-y-auto px-2 py-1">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-0.5">
                      {sections.map((section, idx) => {
                        const config = SECTION_CONFIG[section.type];
                        if (!config) return null;
                        const state = sectionStates.find((s) => s.id === section.id);
                        return (
                          <SortableSectionItem
                            key={section.id}
                            section={section}
                            idx={idx}
                            config={config}
                            hasData={state?.hasData}
                            isActive={selectedSectionId === section.id}
                            isEditable={isEditable}
                            canDelete={canDeleteSection(section)}
                            onSelect={scrollToSection}
                            onDelete={confirmDeleteSection}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>

              {/* MA-UI-ALIGN-062: Premium Control Stack — matches FunnelStepsPanel footer */}
              <div className="flex-shrink-0 p-3 border-t border-white/10 space-y-2">

                {/* + New Section — PRIMARY CTA (gradient button matching FunnelStepsPanel + New Step) */}
                {isEditable && (
                  <Popover open={addPickerOpen} onOpenChange={setAddPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        className={cn(
                          "w-full h-9 text-sm font-medium",
                          "bg-gradient-to-b from-indigo-500/90 to-indigo-600/90",
                          "shadow-md shadow-indigo-500/20",
                          "text-white border-0",
                          "rounded-lg",
                          "hover:from-indigo-500/95 hover:to-indigo-600/95",
                          "hover:shadow-lg hover:shadow-indigo-500/25",
                          "transition-all duration-150"
                        )}
                        data-testid="add-section-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Section
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-1.5 bg-slate-900 border-white/10" side="right" align="end">
                      <div className="text-xs text-slate-500 px-2 py-1 mb-0.5">Add a section</div>
                      <div className="max-h-72 overflow-y-auto space-y-0.5">
                        {SECTION_ORDER.map((type) => {
                          const cfg = SECTION_CONFIG[type];
                          if (!cfg) return null;
                          const TypeIcon = cfg.icon;
                          const blocked = SINGLETON_TYPES.has(type) && existingSingletons.has(type);
                          return (
                            <button
                              key={type}
                              disabled={blocked}
                              onClick={() => !blocked && handleAddSection(type)}
                              className={cn(
                                "w-full flex items-center gap-2 h-8 px-2 rounded-md text-sm transition-colors",
                                blocked
                                  ? "text-slate-600 cursor-not-allowed"
                                  : "text-slate-300 hover:bg-white/10 hover:text-slate-100"
                              )}
                              data-testid={`section-picker-${type}`}
                            >
                              <TypeIcon className={cn("h-3.5 w-3.5", blocked ? "text-slate-600" : cfg.color)} />
                              <span className="flex-1 text-left">{cfg.label}</span>
                              {blocked && <span className="text-[10px] text-slate-600">Added</span>}
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {/* Builder | Preview segmented toggle — matches FunnelStepsPanel Canvas/Visualization */}
                <div
                  className={cn(
                    "relative flex items-center h-7 p-0.5 rounded-full",
                    "bg-slate-800/60",
                    "ring-1 ring-slate-700/40"
                  )}
                  data-testid="canvas-mode-toggle"
                >
                  {/* Sliding thumb */}
                  <div
                    className={cn(
                      "absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded-full",
                      "bg-gradient-to-b from-indigo-500/80 to-indigo-600/80",
                      "shadow-sm shadow-indigo-500/20",
                      "transition-transform duration-200 ease-out",
                      canvasMode === "preview"
                        ? "translate-x-[calc(100%+2px)]"
                        : "translate-x-0"
                    )}
                    style={{ left: '2px' }}
                  />

                  <button
                    type="button"
                    onClick={() => canvasMode !== "builder" && setCanvasMode("builder")}
                    className={cn(
                      "relative z-10 flex-1 flex items-center justify-center gap-1.5 h-full rounded-full",
                      "text-[11px] font-medium transition-all duration-200",
                      canvasMode === "builder"
                        ? "text-white"
                        : "text-slate-500 hover:text-slate-400"
                    )}
                    data-testid="toggle-builder-btn"
                  >
                    <LayoutGrid className="h-3 w-3" />
                    <span>Builder</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => canvasMode !== "preview" && setCanvasMode("preview")}
                    className={cn(
                      "relative z-10 flex-1 flex items-center justify-center gap-1.5 h-full rounded-full",
                      "text-[11px] font-medium transition-all duration-200",
                      canvasMode === "preview"
                        ? "text-white"
                        : "text-slate-500 hover:text-slate-400"
                    )}
                    data-testid="toggle-preview-btn"
                  >
                    <Monitor className="h-3 w-3" />
                    <span>Preview</span>
                  </button>
                </div>
              </div>
            </div>

            {/* ── CENTER CARD: Canvas — flex-1, hero glow ── */}
            <div className={cn(
              "flex-1 min-w-[480px] flex flex-col overflow-hidden",
              "rounded-2xl border border-white/[0.08]",
              "bg-gradient-to-b from-white/[0.05] to-white/[0.02]",
              "backdrop-blur-xl",
              "shadow-[0_0_60px_-12px_rgba(99,102,241,0.15)]"
            )} data-testid="lp-builder-center-panel">
              {/* Canvas header — matches VisualFunnelBuilder canvas header */}
              <div className="px-4 py-2.5 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4 text-indigo-400" />
                    <h2 className="text-sm font-medium text-slate-200">Canvas</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Desktop / Mobile toggle */}
                    <div className="inline-flex h-6 items-center gap-0.5 bg-white/[0.06] rounded-md p-0.5">
                      <button onClick={() => setPreviewMode("desktop")}
                        className={cn("h-5 px-1.5 rounded-sm transition-colors", previewMode === "desktop" ? "bg-white/[0.1] text-white" : "text-slate-500 hover:text-slate-300")}
                        data-testid="preview-desktop-toggle"><Monitor className="w-3 h-3" /></button>
                      <button onClick={() => setPreviewMode("mobile")}
                        className={cn("h-5 px-1.5 rounded-sm transition-colors", previewMode === "mobile" ? "bg-white/[0.1] text-white" : "text-slate-500 hover:text-slate-300")}
                        data-testid="preview-mobile-toggle"><Smartphone className="w-3 h-3" /></button>
                    </div>
                    {/* Status badge */}
                    <Badge className={cn("h-5 text-[10px] border-0 px-2",
                      verStatus === "draft" ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"
                    )} data-testid="preview-status">{verStatus === "draft" ? "Draft" : "Published"}</Badge>
                  </div>
                </div>
              </div>

              {!activeVersion ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <Layers className="w-10 h-10 text-indigo-400/30 mb-4" />
                  <h3 className="text-sm font-semibold text-white mb-1.5">No Version Available</h3>
                  <p className="text-xs text-slate-500 max-w-xs mb-5">Create a draft version to start editing.</p>
                  <Button onClick={handleCreateDraft} disabled={creatingDraft}
                    className="h-8 px-4 text-xs bg-gradient-to-b from-indigo-500 to-indigo-600 shadow-md shadow-indigo-500/25 text-white border-0 rounded-md"
                    data-testid="create-first-draft">
                    {creatingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}Create First Version
                  </Button>
                </div>
              ) : (
                <ScrollArea className="flex-1">
                  <div className={cn(
                    "mx-auto transition-all duration-300",
                    previewMode === "mobile" ? "max-w-[375px]" : "max-w-full",
                    // MA-LP-RENDER-ALIGN-007: Preview bg matches served LP page bg (#0b1220)
                    canvasMode === "preview" && "bg-[#0b1220]"
                  )}>
                    {sections.map((section) => (
                      <SectionPreviewBlock key={section.id} sectionKey={section.type} data={section.data} isSelected={selectedSectionId === section.id}
                        onClick={() => setSelectedSectionId(section.id)} isPreviewMode={canvasMode === "preview"}
                        allSectionData={canvasMode === "preview" ? Object.fromEntries(sections.map(s => [s.type, s.data])) : undefined} previewMode={previewMode} />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* ── RIGHT CARD: Inspector — 340px glass card ── */}
            <div className={cn(
              "w-[340px] min-w-[340px] flex-shrink-0 flex flex-col overflow-hidden",
              "rounded-2xl border border-white/[0.08]",
              "bg-gradient-to-b from-white/[0.04] to-white/[0.02]",
              "backdrop-blur-xl"
            )} data-testid="lp-builder-right-panel">
              {/* Inspector header — matches FunnelPropertiesPanel */}
              <div className="px-5 py-3 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-indigo-400" />
                    <h2 className="text-sm font-semibold text-slate-100">Properties</h2>
                  </div>
                  {!isEditable && activeVersion && (
                    <Badge className="h-5 text-[10px] bg-amber-500/20 text-amber-300 border-0 px-2">Read Only</Badge>
                  )}
                </div>
              </div>

              {!isEditable && activeVersion ? (
                <div className="flex flex-col flex-1 overflow-hidden">
                  <div className="px-5 py-3 border-b border-white/[0.06] bg-amber-500/[0.04]">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-amber-300 font-medium">View-Only Mode</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">This version is {verStatus}. Create a draft to enable editing.</p>
                        <Button size="sm" onClick={handleCreateDraft} disabled={creatingDraft}
                          className="mt-2 h-6 text-[10px] bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30" data-testid="inspector-create-draft">
                          {creatingDraft ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Copy className="w-3 h-3 mr-1" />}Create Draft
                        </Button>
                      </div>
                    </div>
                  </div>
                  <SectionInspector sectionId={selectedSectionId} sectionKey={selectedSectionType} sectionData={selectedSec?.data} onFieldChange={() => {}} isSaving={false} isEditable={false} />
                </div>
              ) : activeVersion ? (
                <SectionInspector sectionId={selectedSectionId} sectionKey={selectedSectionType} sectionData={selectedSec?.data} onFieldChange={handleSectionChange} isSaving={saving} isEditable={true} />
              ) : (
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="text-center">
                    <Layers className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">Create a version to start editing</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === "settings" && (
          <LPSettingsView
            lp={lp}
            lpName={lpName}
            setLpName={setLpName}
            onRenameSave={handleRenameSave}
            isRenaming={isRenaming}
            activeVersion={activeVersion}
            onArchive={() => setArchiveConfirmOpen(true)}
            onSystemSlugUpdate={(newSlug) => setLp(prev => ({ ...prev, system_slug: newSlug }))}
            brandConfig={brandConfig}
            onThemeChange={handleThemeChange}
          />
        )}

        {/* ── HISTORY TAB ── */}
        {activeTab === "history" && (
          <VersionHistoryView
            versions={versions}
            activeVersion={activeVersion}
            onSwitchVersion={handleSwitchVersion}
          />
        )}

        {/* ── ANALYTICS TAB (Coming Soon) ── */}
        {activeTab === "analytics" && <ComingSoonView tabName="Analytics" icon={BarChart3} />}

        {/* ── EMBED TAB (MA-LP-PROD-012) ── */}
        {activeTab === "embed" && <EmbedTabView versions={versions} lp={lp} />}
      </div>

      {/* Delete Section Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-slate-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">
              Delete {deleteTarget && SECTION_CONFIG[deleteTarget.type]?.label}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {deleteTarget?.type === "hero"
                ? "The Hero section is typically the first thing visitors see. Removing it may significantly impact your page."
                : deleteTarget?.type === "final_cta"
                ? "This is your call-to-action section. Removing it may reduce conversions."
                : "This action will remove the section and its content."}
              {" "}This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-white/10 text-slate-300 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && executeDelete(deleteTarget)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-section"
            >
              Delete Section
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Landing Page Confirmation Dialog */}
      <AlertDialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
        <AlertDialogContent className="bg-slate-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">
              Archive this Landing Page?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will hide <span className="text-slate-200 font-medium">{lp?.name}</span> from all listings and deactivate its published version.
              The page will no longer be served to visitors. Version history and data are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-white/10 text-slate-300 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={archiving}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-archive-lp"
            >
              {archiving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Archive className="h-3 w-3 mr-1" />}
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
